import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BadgeCheck,
  Bell,
  CheckCircle2,
  Code2,
  CreditCard,
  Download,
  Edit3,
  Globe2,
  Laptop2,
  Lock,
  Power,
  PauseCircle,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  Sparkles,
  Trash2,
  XOctagon,
  UserCog,
  Settings2,
  BarChartBig,
  Wand2,
  RefreshCcwDot,
  KeyRound,
  Link2,
  BookOpen,
  Terminal,
  Copy,
  Activity,
  Users,
  CircleAlert,
  Clock3,
  MessageSquare,
  Sliders,
  Headphones,
  Zap,
  ShieldAlert,
  GlobeLock,
  Eye,
} from "lucide-react";
import avatar from "../../user.svg";
import "./settings.css";
import { useLanguage } from "context/LanguageContext";
import settingsService from "services/settingsService";
import { clearAuthState } from "utils/auth";

const tabs = [
  { key: "account", label: "Account & Profile", labelKey: "settings.tabs.account", icon: UserCog },
  { key: "enterprise", label: "Enterprise Admin", labelKey: "settings.tabs.enterprise", icon: Shield },
  { key: "subscription", label: "Subscription", labelKey: "settings.tabs.subscription", icon: CreditCard },
  { key: "defaults", label: "Default Settings", labelKey: "settings.tabs.defaults", icon: SlidersHorizontal },
  { key: "api", label: "API", labelKey: "settings.tabs.api", icon: Code2 },
  { key: "ai", label: "AI Assistant", labelKey: "settings.tabs.ai", icon: Sparkles },
  { key: "notifications", label: "Notifications", labelKey: "settings.tabs.notifications", icon: Bell },
  { key: "security", label: "Security", labelKey: "settings.tabs.security", icon: ShieldCheck },
];
const visibleTabs = tabs.filter((tab) => tab.key === "account");

const defaultAccountProfile = {
  username: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company: "",
  role: "",
  profile_bio: "",
  preferences: {
    language: "English (US)",
    timezone: "Europe/Berlin (GMT+1)",
    date_format: "DD/MM/YYYY",
    currency: "EUR",
  },
  stats: {},
  industry_focus: "",
  location: "",
  expertise_areas: [],
  professional_interests: [],
};

const defaultSecurityStatus = {
  two_factor_enabled: true,
  email_verified: true,
  phone_verified: false,
  password_strength: "Unknown",
};

const accountActions = [
  {
    key: "change_password",
    label: "Change Password",
    description: "Update password",
    labelKey: "settings.account.actions.changePassword",
    descKey: "settings.account.actions.changePassword.desc",
    icon: Lock,
    tone: "primary",
  },
  {
    key: "request_data",
    label: "Request Data",
    description: "Download your data",
    labelKey: "settings.account.actions.requestData",
    descKey: "settings.account.actions.requestData.desc",
    icon: Download,
    tone: "primary",
  },
  {
    key: "deactivate",
    label: "Deactivate",
    description: "Temporary disable",
    labelKey: "settings.account.actions.deactivate",
    descKey: "settings.account.actions.deactivate.desc",
    icon: Power,
    tone: "warning",
  },
  {
    key: "delete_account",
    label: "Delete Account",
    description: "Permanent removal",
    labelKey: "settings.account.actions.deleteAccount",
    descKey: "settings.account.actions.deleteAccount.desc",
    icon: Trash2,
    tone: "danger",
  },
];

const preferences = [
  { key: "language", label: "Language", labelKey: "settings.preferences.language", value: "English (US)" },
  { key: "timezone", label: "Timezone", labelKey: "settings.preferences.timezone", value: "Europe/Berlin (GMT+1)" },
  { key: "date_format", label: "Date Format", labelKey: "settings.preferences.dateFormat", value: "DD/MM/YYYY" },
  { key: "currency", label: "Currency", labelKey: "settings.preferences.currency", value: "EUR (Euro)" },
];

const fallbackProfileMeta = {
  bio: "Experienced product manager with 10+ years in B2B SaaS platforms. Specialized in data analytics, supply chain optimization, and enterprise solutions.",
  industryFocus: "Manufacturing & Supply Chain",
  location: "Berlin, Germany",
  expertiseAreas: ["Product Management", "Data Analytics", "Supply Chain", "B2B SaaS"],
  interests: ["AI/ML Applications", "Predictive Analytics", "Market Intelligence"],
};

const fallbackRecentActivity = [
  { id: "fallback-1", title_key: "settings.activity.fallback.loggedIn", meta_key: "settings.activity.fallback.web", ts: null },
];

const fallbackActiveSessions = [
  {
    id: "fallback-current",
    device_key: "settings.activity.fallback.webBrowser",
    location_key: "settings.activity.fallback.unknownLocation",
    status_key: "settings.activity.current",
    current: true,
    last_seen: null,
  },
];

const defaultSettingsData = {
  language: {
    title: "Language & Regional Settings",
    fields: [
      { label: "Primary Language", value: "Deutsch" },
      { label: "Timezone", value: "Central European Time (GMT+1)", helper: "Current: 15:42 CET" },
      { label: "Number Format", value: "1.234.567,89 (European)", helper: "Preview: 1.234.567,89 €" },
      { label: "Date Format", value: "DD.MM.YYYY (09.11.2025)", helper: "Preview: 09.11.2025" },
      { label: "Default Currency", value: "EUR (€) – Euro", helper: "For price and trade data" },
      { label: "Unit System", value: "Metric (kg, km, °C, L)", helper: "Used for all measurements" },
    ],
  },
  charts: {
    title: "Chart & Visual Preferences",
    fields: [
      { label: "Default Chart Type", value: "Line Chart (Trends)" },
      { label: "Color Theme", value: "Dark Mode (Current)" },
      { label: "Default Time Range", value: "Last Year (12M)" },
      { label: "Axis Scaling", value: "Linear (Standard)" },
    ],
    toggles: [
      { label: "Show raw data values in tooltips", on: true },
      { label: "Display units in tooltips", on: true },
      { label: "Show percentage change", on: false },
      { label: "Enable chart zoom & pan", on: true },
      { label: "Animated chart transitions", on: true },
    ],
  },
  filters: {
    title: "Default Filters & Material Settings",
    favorites: ["Copper", "Aluminum", "Steel", "Lithium", "Cobalt"],
    regions: [
      { label: "European Union", selected: true },
      { label: "NAFTA", selected: true },
      { label: "Asia-Pacific" },
      { label: "MENA" },
      { label: "Latin America" },
    ],
    sectors: [
      { label: "Automotive", selected: true },
      { label: "Electronics", selected: true },
      { label: "Energy" },
      { label: "Construction" },
      { label: "Aerospace" },
    ],
    toggles: [
      { label: "Automatic Material Grouping", helper: "Cluster similar materials automatically in charts", on: true },
      { label: "Smart Material Suggestions", helper: "Get AI-powered recommendations for related materials", on: false },
      { label: "Auto-Apply Filters", helper: "Apply saved filters automatically on page load", on: true },
    ],
  },
  export: {
    title: "Export Defaults",
    fields: [
      { label: "Default Format", value: "CSV (Comma Separated)" },
      { label: "Default Time Period", value: "Last 5 Years" },
      { label: "File Naming Convention", value: "DataFlock_{material}_{date}", helper: "Example: DataFlock_Copper_2025-11-09.csv", hasButton: "Preview" },
    ],
    toggles: [
      { label: "Auto-Export with AI Responses", helper: "Automatically generate files when AI creates charts", on: false },
      { label: "Include Metadata", helper: "Add timestamps and source information to exports", on: true },
      { label: "Compress Large Files", helper: "Automatically ZIP files larger than 10MB", on: true },
    ],
  },
  ai: {
    title: "AI Response Behavior",
    fields: [
      { label: "Response Style", value: "Detailed" },
      { label: "AI Language", value: "Auto (Match UI Language)" },
    ],
    toggles: [
      { label: "Auto-Generate Charts", helper: "Create visualizations automatically in AI responses", on: true },
      { label: "Inline Export Suggestions", helper: "Suggest export options in AI responses", on: false },
      { label: "Material Comparison Suggestions", helper: "Automatically suggest related materials for comparison", on: false },
      { label: "Context-Aware Responses", helper: "AI considers your recent queries and preferences", on: true },
      { label: "Proactive Insights", helper: "Get alerts about trends and anomalies in your data", on: false },
    ],
  },
};

const DEFAULTS_TEMPLATE = {
  language: "English (US)",
  timezone: "Central European Time (GMT+1)",
  number_format: "1,234,567.89 (US)",
  theme: "Dark Mode (Current)",
  chart_type: "Line Chart (Trends)",
  time_range: "Last Year (12M)",
  date_format: "MM/DD/YYYY",
  currency: "USD ($) - US Dollar",
  unit_system: "Imperial (lb, mi, °F, gal)",
  axis_scaling: "Linear (Standard)",
  chart_toggles: {
    show_raw_values: true,
    display_units: true,
    show_percentage_change: false,
    enable_zoom_pan: true,
    animated_transitions: true,
  },
  filters: {
    favorites: ["Copper", "Aluminum", "Steel", "Lithium", "Cobalt"],
    regions: ["European Union", "NAFTA"],
    all_regions: ["European Union", "NAFTA", "Asia-Pacific", "MENA", "Latin America"],
    sectors: ["Automotive", "Electronics"],
    all_sectors: ["Automotive", "Electronics", "Energy", "Construction", "Aerospace"],
    auto_material_grouping: true,
    smart_material_suggestions: false,
    auto_apply_filters: true,
  },
  exports: {
    format: "CSV (Comma Separated)",
    time_period: "Last 5 Years",
    file_naming: "DataFlock_{material}_{date}",
    auto_export_with_ai: false,
    include_metadata: true,
    compress_large_files: true,
  },
  ai: {
    response_style: "Detailed",
    language: "Auto (Match UI Language)",
    auto_generate_charts: true,
    inline_export_suggestions: false,
    material_comparison_suggestions: false,
    context_aware_responses: true,
    proactive_insights: false,
  },
};

const DEFAULTS_OPTIONS = {
  language: ["English (US)", "Deutsch", "中文"],
  timezone: ["Central European Time (GMT+1)", "UTC", "US Eastern Time (GMT-5)"],
  number_format: ["1,234,567.89 (US)", "1.234.567,89 (European)"],
  date_format: ["MM/DD/YYYY", "DD/MM/YYYY", "DD.MM.YYYY"],
  currency: ["USD ($) - US Dollar", "EUR (€) - Euro"],
  unit_system: ["Imperial (lb, mi, °F, gal)", "Metric (kg, km, °C, L)"],
  chart_type: ["Line Chart (Trends)", "Bar Chart", "Candlestick Chart"],
  theme: ["Dark Mode (Current)", "Light Mode"],
  time_range: ["Last Year (12M)", "Last 6 Months", "Last 5 Years"],
  axis_scaling: ["Linear (Standard)", "Logarithmic"],
  export_format: ["CSV (Comma Separated)", "JSON", "XLSX"],
  export_time_period: ["Last 5 Years", "Last Year", "All Time"],
  ai_response_style: ["Detailed", "Concise", "Mixed"],
  ai_language: ["Auto (Match UI Language)", "English", "Deutsch", "中文"],
};

const nextOption = (currentValue, options) => {
  if (!Array.isArray(options) || options.length === 0) return currentValue;
  const idx = options.findIndex((x) => x === currentValue);
  if (idx < 0) return options[0];
  return options[(idx + 1) % options.length];
};

const normalizeDefaults = (incoming) => {
  const merged = {
    ...DEFAULTS_TEMPLATE,
    ...(incoming || {}),
    chart_toggles: {
      ...DEFAULTS_TEMPLATE.chart_toggles,
      ...(((incoming || {}).chart_toggles || {})),
    },
    filters: {
      ...DEFAULTS_TEMPLATE.filters,
      ...(((incoming || {}).filters || {})),
    },
    exports: {
      ...DEFAULTS_TEMPLATE.exports,
      ...(((incoming || {}).exports || {})),
    },
    ai: {
      ...DEFAULTS_TEMPLATE.ai,
      ...(((incoming || {}).ai || {})),
    },
  };

  if (!Array.isArray(merged.filters.favorites)) merged.filters.favorites = [...DEFAULTS_TEMPLATE.filters.favorites];
  if (!Array.isArray(merged.filters.regions)) merged.filters.regions = [...DEFAULTS_TEMPLATE.filters.regions];
  if (!Array.isArray(merged.filters.sectors)) merged.filters.sectors = [...DEFAULTS_TEMPLATE.filters.sectors];
  if (!Array.isArray(merged.filters.all_regions)) merged.filters.all_regions = [...DEFAULTS_TEMPLATE.filters.all_regions];
  if (!Array.isArray(merged.filters.all_sectors)) merged.filters.all_sectors = [...DEFAULTS_TEMPLATE.filters.all_sectors];

  return merged;
};

const apiData = {
  overview: [
    { name: "Data API", desc: "Access to commodity, trade, and modeling data", enabled: true },
    { name: "Forecast API", desc: "Query existing prediction models", enabled: true },
    { name: "News API", desc: "Curated and filtered news feeds", enabled: true },
    { name: "Alert API", desc: "Custom alerts and triggers", enabled: true },
    { name: "Tariff API", desc: "Customs rates and trade restrictions", enabled: false },
  ],
  keys: [
    {
      name: "Production CRM Integration",
      status: "active",
      access: "Read/Write",
      created: "2025-01-15",
      lastUsed: "2 hours ago",
      key: "sk_prod_2k9dj...",
    },
    {
      name: "Development Testing",
      status: "active",
      access: "Read Only",
      created: "2025-02-01",
      lastUsed: "5 minutes ago",
      key: "sk_dev_h7g4f2...",
    },
    {
      name: "Analytics Dashboard",
      status: "active",
      access: "Read Only",
      created: "2024-11-20",
      lastUsed: "1 day ago",
      key: "sk_prod_m3n8k1...",
    },
  ],
  usage: {
    requestsToday: { current: 12847, limit: 50000 },
    dataTransfer: { current: "2.4 GB", limit: "10 GB", percent: 24 },
    rateLimit: { current: "890/min", limit: "1,000/min", percent: 89 },
    monthly: [
      { label: "Data API", value: 324000, percent: 65, color: "#7BD5F5" },
      { label: "Forecast API", value: 89000, percent: 18, color: "#9B7BF5" },
      { label: "News API", value: 56000, percent: 11, color: "#6ADFA0" },
      { label: "Tariff API", value: 31000, percent: 6, color: "#F2E96B" },
    ],
    warning: "You’re at 89% of your rate limit. Consider upgrading your plan for higher limits.",
  },
  roles: [
    { name: "Administrators", desc: "Full API Access • 4 users", enabled: true },
    { name: "Data Analysts", desc: "Data API + Forecast API • 12 users", enabled: true },
    { name: "Procurement Team", desc: "Data API (Read Only) • 28 users", enabled: true },
    { name: "External Partners", desc: "No API Access • 6 users", enabled: false },
  ],
  scopes: [
    { label: "Materials Only", enabled: false },
    { label: "Price Data Only", enabled: false },
    { label: "Forecasts Only", enabled: false },
    { label: "Historical Data Only", enabled: true },
  ],
  resources: [
    { title: "API Documentation", desc: "Complete reference and guides", cta: "View Docs" },
    { title: "Code Examples", desc: "Python, JavaScript, cURL samples", cta: "Browse Examples" },
    { title: "Authentication", desc: "OAuth2 and API key setup", cta: "Learn More" },
    { title: "Sandbox Mode", desc: "Test without affecting live data", cta: "Enable Sandbox" },
  ],
  auditLogs: [
    { ts: "2025-11-09 14:32", key: "sk_prod_2k9dj...", endpoint: "/v1/materials/copper", ip: "192.168.1.100", status: 200 },
    { ts: "2025-11-09 14:31", key: "sk_dev_h7g4f2...", endpoint: "/v1/forecasts/lithium", ip: "10.0.0.54", status: 200 },
    { ts: "2025-11-09 14:29", key: "sk_prod_m3n8k1...", endpoint: "/v1/news/feed", ip: "172.16.0.12", status: 200 },
    { ts: "2025-11-09 14:26", key: "sk_prod_2k9dj...", endpoint: "/v1/materials/steel", ip: "192.168.1.100", status: 429 },
    { ts: "2025-11-09 14:25", key: "sk_dev_h7g4f2...", endpoint: "/v1/tariffs/eu", ip: "10.0.0.54", status: 200 },
  ],
  quickstart: `curl -X GET "https://api.materialflow.com/v1/materials/copper/prices" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`,
};

const aiSettings = {
  interaction: [
    { label: "Default Mode", value: "Chat", helper: "Wie der AI Assistant beim Öffnen startet" },
    { label: "Auto-Open beim Start", toggle: true, helper: "Automatisches Öffnen beim Plattformstart" },
    { label: "Sidebar Position merken", toggle: true, helper: "Merkt sich Position und Größe der Sidebar" },
    { label: "Fixierte Ansicht", toggle: false, helper: "Verhindert versehentliches Schließen im Analysenmodus" },
  ],
  response: [
    { label: "Answer Style", value: "Mixed – dynamic", helper: "Wie ausführlich sollen Antworten sein" },
    { label: "Include Visuals", value: "Only when I ask", helper: "Charts und Grafiken in Antworten einbinden" },
    { label: "Confidence Threshold", slider: 75, helper: "Bei geringerer Sicherheit wird ein Hinweis angezeigt" },
  ],
  context: [
    { label: "Page Context Access", toggle: false, helper: "Inhalte der aktuell geöffneten Seite verwenden" },
    { label: "User Profile Integration", toggle: true, helper: "Zugriff auf Profildaten, Interessen, Branche, Märkte" },
    { label: "Use Historical Activity", toggle: false, helper: "Frühere Chats und Aktivitäten zur Kontexterweiterung" },
  ],
  execution: [
    { label: "Ask before every action", helper: "Maximale Kontrolle – jede Aktion muss bestätigt werden", selected: false },
    { label: "Ask once per session", helper: "Empfohlen – erste Aktion bestätigen, dann freie Ausführung", selected: true },
    { label: "Allow full control", helper: "KI kann alle Aktionen eigenständig durchführen", selected: false },
    { label: "No action execution", helper: "Nur Informationen bereitstellen, keine Aktionen", selected: false },
  ],
  models: [
    { label: "Dataflock v2", status: "Active", desc: "Umfassend und kontextsensitive – optimal für komplexe Analysen" },
    { label: "Dataflock v1", status: "", desc: "Schnell, weniger Kontext – ideal für einfache Fragen" },
    { label: "OpenAI GPT-4", status: "Pro", desc: "Premium-Modell für Pro-User mit erweiterten Fähigkeiten" },
  ],
  languageTone: [
    { label: "Preferred Language", value: "Deutsch" },
    { label: "Tone", value: "Neutral" },
  ],
  consent: [
    { label: "Freigabe zur Modellverbesserung", toggle: false, helper: "Eigene Eingaben zur Verbesserung der Modelle verwenden" },
    { label: "Quick-Feedback aktivieren", toggle: true, helper: "👍/👎 unter jeder Antwort anzeigen" },
  ],
};

const LANGUAGE_COPY = {
  en: {
    label: "English",
    preferenceValue: "English (US)",
    defaultFields: [
      { label: "Primary Language", value: "English (US)" },
      { label: "Timezone", value: "Central European Time (GMT+1)", helper: "Current: 15:42 CET" },
      { label: "Number Format", value: "1,234,567.89 (US)", helper: "Preview: 1,234,567.89 €" },
      { label: "Date Format", value: "MM/DD/YYYY (11/09/2025)", helper: "Preview: 11/09/2025" },
      { label: "Default Currency", value: "USD ($) – US Dollar", helper: "For price and trade data" },
      { label: "Unit System", value: "Imperial (lb, mi, °F, gal)", helper: "Used for all measurements" },
    ],
    aiLanguageTone: [
      { label: "Preferred Language", value: "English" },
      { label: "Tone", value: "Neutral" },
    ],
  },
  de: {
    label: "Deutsch",
    preferenceValue: "Deutsch",
    defaultFields: [
      { label: "Primary Language", value: "Deutsch" },
      { label: "Timezone", value: "Mitteleuropäische Zeit (GMT+1)", helper: "Aktuell: 15:42 CET" },
      { label: "Number Format", value: "1.234.567,89 (European)", helper: "Vorschau: 1.234.567,89 €" },
      { label: "Date Format", value: "DD.MM.YYYY (09.11.2025)", helper: "Vorschau: 09.11.2025" },
      { label: "Default Currency", value: "EUR (€) – Euro", helper: "Für Preis- und Handelsdaten" },
      { label: "Unit System", value: "Metrisch (kg, km, °C, L)", helper: "Wird für alle Messungen genutzt" },
    ],
    aiLanguageTone: [
      { label: "Preferred Language", value: "Deutsch" },
      { label: "Tone", value: "Neutral" },
    ],
  },
  zh: {
    label: "中文",
    preferenceValue: "中文",
    defaultFields: [
      { label: "Primary Language", value: "中文" },
      { label: "Timezone", value: "中国标准时间 (GMT+8)", helper: "当前: 15:42 CST" },
      { label: "Number Format", value: "1,234,567.89 (中文)", helper: "预览: 1,234,567.89 ¥" },
      { label: "Date Format", value: "YYYY/MM/DD (2025/11/09)", helper: "预览: 2025/11/09" },
      { label: "Default Currency", value: "CNY (¥) – 人民币", helper: "用于价格与交易数据" },
      { label: "Unit System", value: "公制 (kg, km, °C, L)", helper: "用于所有测量" },
    ],
    aiLanguageTone: [
      { label: "Preferred Language", value: "中文" },
      { label: "Tone", value: "中性" },
    ],
  },
};

const LANGUAGE_CHOICES = Object.entries(LANGUAGE_COPY).map(([code, meta]) => ({
  code,
  label: meta.label,
}));

const securityData = {
  dataPrivacy: {
    rights: "Ihre Rechte nach DSGVO",
    summary: "Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit und Widerspruch.",
    dataOverview: [
      { label: "Profildaten", size: "2.4 MB" },
      { label: "Gespeicherte Modelle", size: "15.2 MB" },
      { label: "API Logs", size: "1.8 MB" },
      { label: "Aktivitätsdaten", size: "8.7 MB" },
      { label: "Chat History", size: "3.1 MB" },
      { label: "Präferenzen", size: "0.3 MB" },
    ],
  },
  residency: {
    regions: [
      { label: "Frankfurt, Germany (Primary)", code: "eu-central-1", active: true },
      { label: "Amsterdam, Netherlands (Backup)", code: "eu-west-1", active: true },
      { label: "US East (Disabled)", code: "us-east-1", active: false },
    ],
    compliance: ["GDPR Compliant", "ISO 27001", "SOC 2 Type II"],
  },
  twofa: {
    qr: "JBSWY3DPEHPK3PXP",
    backupCodes: 8,
  },
  sessions: [
    { device: "MacBook Pro 16\" (M3)", meta: "Chrome 120 • Frankfurt, Germany", ip: "185.42.137.88", last: "Just now", current: true },
    { device: "iPhone 15 Pro", meta: "Safari Mobile • Munich, Germany", ip: "78.52.214.193", last: "2 hours ago" },
    { device: "Windows Desktop (Work)", meta: "Edge 120 • Berlin, Germany", ip: "192.168.1.45", last: "Yesterday" },
  ],
  logs: [
    { title: "Successful login", meta: "MacBook Pro · Frankfurt, DE", time: "5 minutes ago", tone: "good" },
    { title: "2FA enabled", meta: "MacBook Pro · Frankfurt, DE", time: "2 hours ago", tone: "good" },
    { title: "Login from new device", meta: "iPhone 15 Pro · Munich, DE", time: "Yesterday", tone: "warn" },
    { title: "Password changed", meta: "MacBook Pro · Frankfurt, DE", time: "3 days ago", tone: "good" },
    { title: "Failed login attempt", meta: "Unknown · London, UK", time: "5 days ago", tone: "danger" },
  ],
  password: {
    lastChanged: "3 Tage her",
    rules: ["Mindestens 12 Zeichen", "Groß- und Kleinbuchstaben", "Mindestens eine Zahl", "Mindestens ein Sonderzeichen"],
  },
  privacy: [
    { label: "Analytics Tracking", helper: "Anonymisierte Nutzungsdaten zur Produktverbesserung", toggle: false },
    { label: "Marketing Communications", helper: "Newsletter und Produktupdates per E-Mail", toggle: true },
    { label: "Third-party Integrations", helper: "Zugriff für verbundene Apps und Services", action: "Manage (3)" },
    { label: "Profile Visibility", helper: "Wer kann Ihr Profil sehen", action: "Only my team" },
  ],
  retention: [
    { label: "Automatische Löschung", helper: "Alte Daten automatisch löschen nach", value: "90 Tage" },
    { label: "Chat History Retention", helper: "AI Assistant Konversationen speichern", value: "1 Jahr" },
    { label: "Backup-Einstellungen", helper: "Automatische Backups Ihrer Daten", toggle: false },
  ],
};

const subscriptionData = {
  currentPlan: {
    name: "Pro Plan",
    status: "Active",
    description: "Professional features for growing teams",
    price: "€249",
    cadence: "per month",
    billingInterval: "Monthly",
    nextRenewal: "December 15, 2024",
    users: "8 / 15",
    exportLimit: "850 / 2,000 per month",
  },
  availablePlans: [
    {
      name: "Core",
      price: "€49",
      cadence: "per month",
      bullets: ["Up to 3 users", "500 exports/month", "Basic support", "Standard data access"],
    },
    {
      name: "Pro",
      price: "€249",
      cadence: "per month",
      tag: "Current",
      bullets: ["Up to 15 users", "2,000 exports/month", "Priority support", "Advanced analytics"],
    },
    {
      name: "Enterprise",
      price: "€999",
      cadence: "per month",
      bullets: ["Unlimited users", "Unlimited exports", "24/7 support", "Custom integrations"],
    },
    {
      name: "Custom",
      price: "Contact",
      cadence: "us for pricing",
      bullets: ["Custom solutions", "Dedicated support", "SLA guarantees", "White-label options"],
    },
  ],
  addons: [
    {
      name: "API Access",
      price: "€99/mo",
      desc: "Full REST API access with 100k requests/month",
      enabled: true,
    },
    {
      name: "Forecast Module",
      price: "€149/mo",
      desc: "AI-powered price forecasting and trend analysis",
      enabled: true,
    },
    {
      name: "Additional Users",
      price: "€29/user",
      desc: "Add more team members beyond your plan limit",
      enabled: false,
    },
  ],
  billingDetails: {
    addressTitle: "Billing Address",
    address: ["DataFlock GmbH", "Hauptstraße 123", "10115 Berlin, Germany"],
    paymentTitle: "Payment Method",
    payment: "VISA  •••• •••• •••• 4242",
    status: "Last payment successful",
    statusMeta: "November 15, 2024 · €249.00",
    vatNumber: "DE123456789",
  },
  invoices: [
    { date: "Nov 15, 2024", label: "Invoice #1000", amount: "€249.00", status: "Paid" },
    { date: "Oct 15, 2024", label: "Invoice #999", amount: "€249.00", status: "Paid" },
    { date: "Sep 15, 2024", label: "Invoice #998", amount: "€249.00", status: "Paid" },
    { date: "Aug 15, 2024", label: "Invoice #997", amount: "€249.00", status: "Paid" },
  ],
};

const secondaryPanels = {
  enterprise: {
    description: "Control workspace access, enforce SSO/SCIM, and keep audit visibility for every admin action.",
    highlights: [
      { icon: Shield, title: "Admin guardrails", text: "Owner approvals, SCIM groups, and SSO enforcement." },
      { icon: CheckCircle2, title: "Role templates", text: "Curated permissions for admins, editors, and viewers." },
      { icon: Sparkles, title: "Automation", text: "Auto-provision teams with least-privilege defaults." },
    ],
  },
  subscription: {
    description: "Track plan usage, download invoices, and manage seats without leaving the workspace.",
    highlights: [
      { icon: CreditCard, title: "Plans & seats", text: "Assign seats, set limits, and view renewals at a glance." },
      { icon: Download, title: "Billing exports", text: "Pull invoices, VAT notes, and receipts anytime." },
      { icon: Bell, title: "Renewal alerts", text: "Get proactive reminders before renewals or overages." },
    ],
  },
  defaults: {
    description: "Define workspace-level defaults for language, time, templates, and collaboration etiquette.",
    highlights: [
      { icon: Globe2, title: "Localization", text: "Region-aware language, timezone, and date formats." },
      { icon: SlidersHorizontal, title: "Templates", text: "Preset dashboards, reports, and onboarding flows." },
      { icon: Bell, title: "Collaboration", text: "Commenting, mentions, and share defaults by team." },
    ],
  },
  api: {
    description: "Manage API keys, webhooks, and environment gates from a single surface.",
    highlights: [
      { icon: Code2, title: "Key rotation", text: "Create, pause, and roll tokens with IP allowlists." },
      { icon: ShieldCheck, title: "Policies", text: "Rate limits, scopes, and audit trails for every call." },
      { icon: Download, title: "Webhooks", text: "Reliable delivery with replay and signing secrets." },
    ],
  },
  ai: {
    description: "Tune AI assistants with safe defaults, data-privacy guardrails, and prompt visibility.",
    highlights: [
      { icon: Sparkles, title: "Assistant modes", text: "Switch between creative, concise, or factual profiles." },
      { icon: Shield, title: "Data controls", text: "Opt out of training data and mask sensitive fields." },
      { icon: Bell, title: "Observability", text: "Trace prompts, responses, and safety triggers." },
    ],
  },
  notifications: {
    description: "Choose when and how you get notified across devices and channels.",
    highlights: [
      { icon: Bell, title: "Channels", text: "Fine-tune email, in-app, and mobile push preferences." },
      { icon: SlidersHorizontal, title: "Rules", text: "Group by priority, mute windows, and digest schedules." },
      { icon: ShieldCheck, title: "Trust", text: "Signed alerts with device posture awareness." },
    ],
  },
  security: {
    description: "Increase confidence with strong authentication, device trust, and rapid response controls.",
    highlights: [
      { icon: ShieldCheck, title: "Zero-trust", text: "Session limits, device posture checks, and IP controls." },
      { icon: Lock, title: "Access hygiene", text: "Password policies, rotation windows, and recovery paths." },
      { icon: Power, title: "Rapid response", text: "Kill switches for tokens, sessions, and connected apps." },
    ],
  },
};

const SettingsPage = () => {
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();
  const [activeTab, setActiveTab] = useState("account");
  const [accountProfile, setAccountProfile] = useState(defaultAccountProfile);
  const [accountDraft, setAccountDraft] = useState(defaultAccountProfile);
  const [accountEditMode, setAccountEditMode] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountError, setAccountError] = useState("");
  const [accountSuccess, setAccountSuccess] = useState("");
  const [preferencesEditMode, setPreferencesEditMode] = useState(false);
  const [preferencesSaving, setPreferencesSaving] = useState(false);
  const [preferencesDraft, setPreferencesDraft] = useState(defaultAccountProfile.preferences);
  const [activityLoading, setActivityLoading] = useState(false);
  const [recentActivity, setRecentActivity] = useState(fallbackRecentActivity);
  const [activeSessions, setActiveSessions] = useState(fallbackActiveSessions);
  const [securityStatus, setSecurityStatus] = useState(defaultSecurityStatus);
  const [securityDataOverview, setSecurityDataOverview] = useState(securityData.dataPrivacy.dataOverview || []);
  const [defaultsDraft, setDefaultsDraft] = useState(DEFAULTS_TEMPLATE);
  const [defaultsLoading, setDefaultsLoading] = useState(false);
  const [defaultsSaving, setDefaultsSaving] = useState(false);
  const defaultsSaveTimerRef = useRef(null);
  const toastTimerRef = useRef(null);
  const [toast, setToast] = useState(null);
  const [dialog, setDialog] = useState({
    open: false,
    type: "confirm",
    action: "",
    title: "",
    message: "",
    tone: "primary",
    confirmLabel: "Confirm",
    cancelLabel: "Cancel",
    inputValue: "",
    inputPlaceholder: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    error: "",
    loading: false,
  });
  const activeTabMeta = useMemo(() => tabs.find((t) => t.key === activeTab), [activeTab]);
  const activeLanguage = LANGUAGE_COPY[language] || LANGUAGE_COPY.en;
  const normalizeActivityToken = (value) => String(value || "").trim().toLowerCase();
  const staticKeyFor = (value) => {
    if (typeof value !== "string" || !value.trim()) return null;
    const slug = value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 80);
    return slug ? `settings.static.${slug}` : null;
  };
  const localizeStatic = (value) => {
    if (typeof value !== "string") return value;
    const key = staticKeyFor(value);
    if (!key) return value;
    return t(key, { defaultValue: value });
  };
  const tr = (value) => {
    if (typeof value !== "string") return value;
    const translated = t(value, { defaultValue: value });
    if (translated !== value) return translated;
    return localizeStatic(value);
  };
  const localizeActivityTitle = (item) => {
    if (item?.title_key) return t(item.title_key);
    const raw = String(item?.title || "").trim();
    const normalized = normalizeActivityToken(raw);
    if (["logged in", "login", "signed in"].includes(normalized)) return t("settings.activity.fallback.loggedIn", { defaultValue: raw });
    if (normalized === "generated ai report") return t("settings.activity.fallback.generatedAiReport", { defaultValue: raw });
    if (normalized === "custom report") return t("settings.activity.fallback.customReport", { defaultValue: raw });
    return raw || "—";
  };
  const localizeActivityMeta = (item) => {
    if (item?.meta_key) return t(item.meta_key);
    const raw = String(item?.meta || "").trim();
    const normalized = normalizeActivityToken(raw);
    if (normalized === "web") return t("settings.activity.fallback.web", { defaultValue: raw });
    if (normalized === "web browser") return t("settings.activity.fallback.webBrowser", { defaultValue: raw });
    return raw || "—";
  };
  const localizeSessionDevice = (session) => {
    if (session?.device_key) return t(session.device_key);
    const raw = String(session?.device || "").trim();
    const normalized = normalizeActivityToken(raw);
    if (normalized === "web") return t("settings.activity.fallback.web", { defaultValue: raw });
    if (normalized === "web browser" || normalized === "browser") return t("settings.activity.fallback.webBrowser", { defaultValue: raw });
    return raw || "—";
  };
  const localizeSessionLocation = (session) => {
    if (session?.location_key) return t(session.location_key);
    const raw = String(session?.location || "").trim();
    const normalized = normalizeActivityToken(raw);
    if (normalized === "unknown location") return t("settings.activity.fallback.unknownLocation", { defaultValue: raw });
    return raw || "—";
  };

  const showToast = (message, tone = "info") => {
    if (!message) return;
    setToast({ message, tone });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 3500);
  };

  const closeDialog = () => {
    setDialog({
      open: false,
      type: "confirm",
      action: "",
      title: "",
      message: "",
      tone: "primary",
      confirmLabel: "Confirm",
      cancelLabel: "Cancel",
      inputValue: "",
      inputPlaceholder: "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      error: "",
      loading: false,
    });
  };

  const loadAccountProfile = async () => {
    setAccountLoading(true);
    setAccountError("");
    try {
      const data = await settingsService.getAccount();
      const merged = {
        ...defaultAccountProfile,
        ...(data || {}),
        preferences: {
          ...defaultAccountProfile.preferences,
          ...((data || {}).preferences || {}),
        },
      };
      setAccountProfile(merged);
      setAccountDraft(merged);
      setPreferencesDraft({
        ...defaultAccountProfile.preferences,
        ...merged.preferences,
      });
    } catch (error) {
      setAccountError(error?.data?.detail || error?.message || t("settings.errors.loadAccount"));
    } finally {
      setAccountLoading(false);
    }
  };

  const loadActivity = async () => {
    setActivityLoading(true);
    try {
      const data = await settingsService.getActivity();
      const recent = Array.isArray(data?.recent) && data.recent.length ? data.recent : fallbackRecentActivity;
      const sessions = Array.isArray(data?.sessions) && data.sessions.length ? data.sessions : fallbackActiveSessions;
      setRecentActivity(recent);
      setActiveSessions(sessions);
    } catch (error) {
      setRecentActivity(fallbackRecentActivity);
      setActiveSessions(fallbackActiveSessions);
    } finally {
      setActivityLoading(false);
    }
  };

  const loadSecurity = async () => {
    try {
      const data = await settingsService.getSecurity();
      setSecurityStatus({
        ...defaultSecurityStatus,
        ...(data || {}),
      });
    } catch (error) {
      setSecurityStatus(defaultSecurityStatus);
    }
  };

  const loadSecurityDataOverview = async () => {
    try {
      const data = await settingsService.getSecurityDataOverview();
      const items = Array.isArray(data)
        ? data
        : (Array.isArray(data?.items) ? data.items : []);
      if (!items.length) {
        setSecurityDataOverview(securityData.dataPrivacy.dataOverview || []);
        return;
      }
      setSecurityDataOverview(
        items.map((item) => ({
          label: item?.label || "Unknown",
          size: item?.size_human || item?.size || "0 B",
        }))
      );
    } catch (error) {
      setSecurityDataOverview(securityData.dataPrivacy.dataOverview || []);
    }
  };

  const loadDefaults = async () => {
    setDefaultsLoading(true);
    try {
      const data = await settingsService.getDefaults();
      setDefaultsDraft(normalizeDefaults(data));
    } catch (error) {
      setDefaultsDraft(normalizeDefaults(DEFAULTS_TEMPLATE));
    } finally {
      setDefaultsLoading(false);
    }
  };

  const persistDefaults = async (nextState) => {
    try {
      setDefaultsSaving(true);
      await settingsService.updateDefaults(nextState);
    } catch (error) {
      showToast(error?.data?.detail || error?.message || t("settings.errors.saveDefaults"), "error");
    } finally {
      setDefaultsSaving(false);
    }
  };

  const queueDefaultsSave = (nextState) => {
    if (defaultsSaveTimerRef.current) {
      clearTimeout(defaultsSaveTimerRef.current);
    }
    defaultsSaveTimerRef.current = setTimeout(() => {
      persistDefaults(nextState);
    }, 350);
  };

  const updateDefaultsState = (updater) => {
    setDefaultsDraft((prev) => {
      const next = normalizeDefaults(typeof updater === "function" ? updater(prev) : updater);
      queueDefaultsSave(next);
      return next;
    });
  };

  useEffect(() => {
    loadAccountProfile();
    loadActivity();
    loadSecurity();
    loadSecurityDataOverview();
    loadDefaults();
    return () => {
      if (defaultsSaveTimerRef.current) {
        clearTimeout(defaultsSaveTimerRef.current);
      }
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const handleAccountInputChange = (field, value) => {
    setAccountDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveAccount = async () => {
    setAccountSaving(true);
    setAccountError("");
    setAccountSuccess("");
    try {
      const payload = {
        ...accountDraft,
        preferences: {
          ...defaultAccountProfile.preferences,
          ...(accountDraft.preferences || {}),
        },
      };
      const saved = await settingsService.updateAccount(payload);
      const merged = {
        ...defaultAccountProfile,
        ...(saved || payload),
        preferences: {
          ...defaultAccountProfile.preferences,
          ...((saved || payload).preferences || {}),
        },
      };
      setAccountProfile(merged);
      setAccountDraft(merged);
      setAccountEditMode(false);
      setAccountSuccess("Account updated successfully.");
    } catch (error) {
      setAccountError(error?.data?.detail || error?.message || t("settings.errors.saveAccount"));
    } finally {
      setAccountSaving(false);
    }
  };

  const userInformation = useMemo(
    () => [
      { label: "Username", labelKey: "settings.account.userInfo.username", key: "username", value: accountDraft.username || accountProfile.username || "" },
      { label: "First Name", labelKey: "settings.account.userInfo.firstName", key: "first_name", value: accountDraft.first_name || accountProfile.first_name || "" },
      { label: "Last Name", labelKey: "settings.account.userInfo.lastName", key: "last_name", value: accountDraft.last_name || accountProfile.last_name || "" },
      { label: "Phone Number", labelKey: "settings.account.userInfo.phoneNumber", key: "phone", value: accountDraft.phone || accountProfile.phone || "" },
      { label: "Company", labelKey: "settings.account.userInfo.company", key: "company", value: accountDraft.company || accountProfile.company || "" },
      { label: "Role", labelKey: "settings.account.userInfo.role", key: "role", value: accountDraft.role || accountProfile.role || "" },
      { label: "E-Mail", labelKey: "settings.account.userInfo.email", key: "email", value: accountDraft.email || accountProfile.email || "" },
    ],
    [accountDraft, accountProfile]
  );

  const preferencesWithLanguage = useMemo(
    () =>
      preferences.map((item) => {
        if (item.key === "language") {
          return { ...item, value: preferencesDraft.language || activeLanguage.preferenceValue };
        }
        if (item.key === "timezone") {
          return { ...item, value: preferencesDraft.timezone || accountProfile.preferences?.timezone || "—" };
        }
        if (item.key === "date_format") {
          return { ...item, value: preferencesDraft.date_format || accountProfile.preferences?.date_format || "—" };
        }
        if (item.key === "currency") {
          return { ...item, value: preferencesDraft.currency || accountProfile.preferences?.currency || "—" };
        }
        return item;
      }),
    [activeLanguage, preferencesDraft, accountProfile]
  );

  const profileMeta = useMemo(() => {
    const expertise = Array.isArray(accountProfile.expertise_areas) ? accountProfile.expertise_areas : [];
    const interests = Array.isArray(accountProfile.professional_interests) ? accountProfile.professional_interests : [];
    const hasDynamicData =
      Boolean((accountProfile.profile_bio || "").trim()) ||
      Boolean((accountProfile.industry_focus || "").trim()) ||
      Boolean((accountProfile.location || "").trim()) ||
      expertise.length > 0 ||
      interests.length > 0;

    if (!hasDynamicData) return fallbackProfileMeta;

    return {
      bio: (accountProfile.profile_bio || "").trim() || "Profile generated from onboarding.",
      industryFocus: (accountProfile.industry_focus || "").trim() || "—",
      location: (accountProfile.location || "").trim() || "—",
      expertiseAreas: expertise.length ? expertise : ["—"],
      interests: interests.length ? interests : ["—"],
    };
  }, [accountProfile]);

  const formatMemberSince = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short" });
  };

  const formatLastLogin = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (isToday) return `${t("settings.account.stats.today", { defaultValue: "Today" })}, ${time}`;
    return d.toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const accountStats = useMemo(() => {
    const stats = accountProfile?.stats || {};
    return [
      { label: "Member Since", labelKey: "settings.account.stats.memberSince", value: formatMemberSince(stats.member_since) },
      { label: "Models Created", labelKey: "settings.account.stats.modelsCreated", value: Number(stats.models_created || 0).toLocaleString() },
      { label: "Reports Generated", labelKey: "settings.account.stats.reportsGenerated", value: Number(stats.reports_generated || 0).toLocaleString() },
      { label: "Last Login", labelKey: "settings.account.stats.lastLogin", value: formatLastLogin(stats.last_login) },
      { label: "Total Sessions", labelKey: "settings.account.stats.totalSessions", value: Number(stats.total_sessions || 0).toLocaleString() },
    ];
  }, [accountProfile, t]);

  const securityChecks = useMemo(() => {
    const twoFactorEnabled = Boolean(securityStatus?.two_factor_enabled);
    const emailVerified = Boolean(securityStatus?.email_verified);
    const phoneVerified = Boolean(securityStatus?.phone_verified);
    const passwordStrengthValue = String(securityStatus?.password_strength || "Unknown");
    const normalizedStrength = passwordStrengthValue.toLowerCase();
    const passwordTone =
      normalizedStrength === "strong" ? "good" : normalizedStrength === "good" ? "warn" : "warn";

    return [
      {
        label: "Two-Factor Auth",
        labelKey: "settings.security.status.twoFactorAuth",
        value: twoFactorEnabled
          ? t("settings.security.status.enabled", { defaultValue: "Enabled" })
          : t("settings.security.status.disabled", { defaultValue: "Disabled" }),
        tone: twoFactorEnabled ? "good" : "warn",
      },
      {
        label: "Email Verified",
        labelKey: "settings.security.status.emailVerified",
        value: emailVerified
          ? t("settings.security.status.verified", { defaultValue: "Verified" })
          : t("settings.security.status.pending", { defaultValue: "Pending" }),
        tone: emailVerified ? "good" : "warn",
      },
      {
        label: "Phone Verified",
        labelKey: "settings.security.status.phoneVerified",
        value: phoneVerified
          ? t("settings.security.status.verified", { defaultValue: "Verified" })
          : t("settings.security.status.pending", { defaultValue: "Pending" }),
        tone: phoneVerified ? "good" : "warn",
      },
      {
        label: "Password Strength",
        labelKey: "settings.security.status.passwordStrength",
        value: t(`settings.security.status.${normalizedStrength}`, { defaultValue: passwordStrengthValue }),
        tone: passwordTone,
      },
    ];
  }, [securityStatus, t]);

  const defaultsLanguageFields = useMemo(
    () => [
      { key: "language", label: "Primary Language", value: defaultsDraft.language },
      { key: "timezone", label: "Timezone", value: defaultsDraft.timezone, helper: "Current: 15:42 CET" },
      { key: "number_format", label: "Number Format", value: defaultsDraft.number_format, helper: "Preview: 1,234,567.89 €" },
      { key: "date_format", label: "Date Format", value: defaultsDraft.date_format, helper: "Preview: 11/09/2025" },
      { key: "currency", label: "Default Currency", value: defaultsDraft.currency, helper: "For price and trade data" },
      { key: "unit_system", label: "Unit System", value: defaultsDraft.unit_system, helper: "Used for all measurements" },
    ],
    [defaultsDraft]
  );

  const defaultsChartFields = useMemo(
    () => [
      { key: "chart_type", label: "Default Chart Type", value: defaultsDraft.chart_type },
      { key: "theme", label: "Color Theme", value: defaultsDraft.theme },
      { key: "time_range", label: "Default Time Range", value: defaultsDraft.time_range },
      { key: "axis_scaling", label: "Axis Scaling", value: defaultsDraft.axis_scaling },
    ],
    [defaultsDraft]
  );

  const defaultsChartToggleFields = useMemo(
    () => [
      { key: "show_raw_values", label: "Show raw data values in tooltips" },
      { key: "display_units", label: "Display units in tooltips" },
      { key: "show_percentage_change", label: "Show percentage change" },
      { key: "enable_zoom_pan", label: "Enable chart zoom & pan" },
      { key: "animated_transitions", label: "Animated chart transitions" },
    ],
    []
  );

  const defaultsFilterToggleFields = useMemo(
    () => [
      { key: "auto_material_grouping", label: "Automatic Material Grouping", helper: "Cluster similar materials automatically in charts" },
      { key: "smart_material_suggestions", label: "Smart Material Suggestions", helper: "Get AI-powered recommendations for related materials" },
      { key: "auto_apply_filters", label: "Auto-Apply Filters", helper: "Apply saved filters automatically on page load" },
    ],
    []
  );

  const defaultsExportFields = useMemo(
    () => [
      { key: "format", label: "Default Format", value: defaultsDraft.exports?.format || "—" },
      { key: "time_period", label: "Default Time Period", value: defaultsDraft.exports?.time_period || "—" },
      { key: "file_naming", label: "File Naming Convention", value: defaultsDraft.exports?.file_naming || "—", helper: "Example: DataFlock_Copper_2025-11-09.csv", hasButton: "Preview" },
    ],
    [defaultsDraft]
  );

  const defaultsExportToggleFields = useMemo(
    () => [
      { key: "auto_export_with_ai", label: "Auto-Export with AI Responses", helper: "Automatically generate files when AI creates charts" },
      { key: "include_metadata", label: "Include Metadata", helper: "Add timestamps and source information to exports" },
      { key: "compress_large_files", label: "Compress Large Files", helper: "Automatically ZIP files larger than 10MB" },
    ],
    []
  );

  const defaultsAiFields = useMemo(
    () => [
      { key: "response_style", label: "Response Style", value: defaultsDraft.ai?.response_style || "—" },
      { key: "language", label: "AI Language", value: defaultsDraft.ai?.language || "—" },
    ],
    [defaultsDraft]
  );

  const defaultsAiToggleFields = useMemo(
    () => [
      { key: "auto_generate_charts", label: "Auto-Generate Charts", helper: "Create visualizations automatically in AI responses" },
      { key: "inline_export_suggestions", label: "Inline Export Suggestions", helper: "Suggest export options in AI responses" },
      { key: "material_comparison_suggestions", label: "Material Comparison Suggestions", helper: "Automatically suggest related materials for comparison" },
      { key: "context_aware_responses", label: "Context-Aware Responses", helper: "AI considers your recent queries and preferences" },
      { key: "proactive_insights", label: "Proactive Insights", helper: "Get alerts about trends and anomalies in your data" },
    ],
    []
  );

  const setDefaultsField = (key, value) => {
    updateDefaultsState((prev) => ({ ...prev, [key]: value }));
  };

  const cycleDefaultsField = (key, options) => {
    const nextValue = nextOption(defaultsDraft[key], options);
    setDefaultsField(key, nextValue);
  };

  const toggleDefaultsChartFlag = (key) => {
    updateDefaultsState((prev) => ({
      ...prev,
      chart_toggles: {
        ...(prev.chart_toggles || {}),
        [key]: !Boolean((prev.chart_toggles || {})[key]),
      },
    }));
  };

  const toggleDefaultsFilterFlag = (key) => {
    updateDefaultsState((prev) => ({
      ...prev,
      filters: {
        ...(prev.filters || {}),
        [key]: !Boolean((prev.filters || {})[key]),
      },
    }));
  };

  const toggleDefaultsExportFlag = (key) => {
    updateDefaultsState((prev) => ({
      ...prev,
      exports: {
        ...(prev.exports || {}),
        [key]: !Boolean((prev.exports || {})[key]),
      },
    }));
  };

  const toggleDefaultsAiFlag = (key) => {
    updateDefaultsState((prev) => ({
      ...prev,
      ai: {
        ...(prev.ai || {}),
        [key]: !Boolean((prev.ai || {})[key]),
      },
    }));
  };

  const cycleDefaultsExportField = (key, options) => {
    const nextValue = nextOption(defaultsDraft.exports?.[key], options);
    updateDefaultsState((prev) => ({
      ...prev,
      exports: { ...(prev.exports || {}), [key]: nextValue },
    }));
  };

  const cycleDefaultsAiField = (key, options) => {
    const nextValue = nextOption(defaultsDraft.ai?.[key], options);
    updateDefaultsState((prev) => ({
      ...prev,
      ai: { ...(prev.ai || {}), [key]: nextValue },
    }));
  };

  const toggleMultiSelect = (collectionKey, value) => {
    updateDefaultsState((prev) => {
      const current = Array.isArray(prev.filters?.[collectionKey]) ? prev.filters[collectionKey] : [];
      const next = current.includes(value)
        ? current.filter((x) => x !== value)
        : [...current, value];
      return {
        ...prev,
        filters: {
          ...(prev.filters || {}),
          [collectionKey]: next,
        },
      };
    });
  };

  const addFavoriteMaterial = () => {
    setDialog({
      open: true,
      type: "prompt",
      action: "add_favorite_material",
      title: "Add favorite material",
      message: "Enter a material name.",
      tone: "primary",
      confirmLabel: "Add",
      cancelLabel: "Cancel",
      inputValue: "",
      inputPlaceholder: "e.g. Nickel",
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      error: "",
      loading: false,
    });
  };

  const exportDefaultsAsJson = () => {
    const blob = new Blob([JSON.stringify(defaultsDraft, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "dataflock-default-settings.json";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const handleResetDefaults = async (section = "all") => {
    try {
      await settingsService.resetDefaults(section);
      await loadDefaults();
    } catch (error) {
      showToast(error?.data?.detail || error?.message || t("settings.errors.resetDefaults"), "error");
    }
  };

  const aiLanguageTone = useMemo(() => activeLanguage.aiLanguageTone, [activeLanguage]);

  const getPreferenceLanguageValue = (code) => {
    return LANGUAGE_COPY[code]?.preferenceValue || LANGUAGE_COPY.en.preferenceValue;
  };

  const handleLanguageChange = async (code) => {
    if (!LANGUAGE_COPY[code]) return;
    const previousLanguageCode = language;
    const previousDraft = preferencesDraft;
    const nextLanguageValue = getPreferenceLanguageValue(code);

    setLanguage(code);
    setPreferencesDraft((prev) => ({
      ...prev,
      language: nextLanguageValue,
    }));

    try {
      const payload = {
        language: nextLanguageValue,
        timezone: previousDraft.timezone || "Europe/Berlin (GMT+1)",
        date_format: previousDraft.date_format || "DD/MM/YYYY",
        currency: previousDraft.currency || "EUR",
      };
      const saved = await settingsService.updatePreferences(payload);
      setAccountProfile((prev) => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          ...saved,
        },
      }));
      setPreferencesDraft((prev) => ({
        ...prev,
        ...saved,
        language: saved?.language || nextLanguageValue,
      }));
    } catch (error) {
      setLanguage(previousLanguageCode);
      setPreferencesDraft(previousDraft);
      showToast(error?.data?.detail || error?.message || t("settings.errors.savePreferences"), "error");
    }
  };

  const getLanguageCodeFromValue = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    const found = LANGUAGE_CHOICES.find((option) => option.label.toLowerCase() === normalized);
    if (found) return found.code;
    if (normalized.includes("de")) return "de";
    if (normalized.includes("zh") || normalized.includes("中文")) return "zh";
    return "en";
  };

  const handleSavePreferences = async () => {
    setPreferencesSaving(true);
    try {
      const payload = {
        language: preferencesDraft.language || "English (US)",
        timezone: preferencesDraft.timezone || "Europe/Berlin (GMT+1)",
        date_format: preferencesDraft.date_format || "DD/MM/YYYY",
        currency: preferencesDraft.currency || "EUR",
      };
      const saved = await settingsService.updatePreferences(payload);
      setAccountProfile((prev) => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          ...saved,
        },
      }));
      setPreferencesDraft((prev) => ({ ...prev, ...saved }));
      setPreferencesEditMode(false);
      const langCode = getLanguageCodeFromValue(saved?.language);
      if (langCode) setLanguage(langCode);
    } catch (error) {
      showToast(error?.data?.detail || error?.message || t("settings.errors.savePreferences"), "error");
    } finally {
      setPreferencesSaving(false);
    }
  };

  const formatRelativeTime = (raw) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "—";
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return t("common.justNow");
    if (diffMin < 60) return t("common.minutesAgo", { count: diffMin });
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return t("common.hoursAgo", { count: diffHr });
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay === 1) return t("common.yesterday");
    return t("common.daysAgo", { count: diffDay });
  };

  const handleRevokeSession = async (session) => {
    if (!session || session.current) return;
    try {
      await settingsService.revokeSession(session.id);
      await loadActivity();
    } catch (error) {
      showToast(error?.data?.detail || error?.message || t("settings.errors.revokeSession"), "error");
    }
  };

  const handleAccountAction = async (actionKey) => {
    if (actionKey === "change_password") {
      setDialog({
        open: true,
        type: "password",
        action: "change_password",
        title: t("settings.dialog.title.changePassword"),
        message: t("settings.dialog.message.changePassword"),
        tone: "primary",
        confirmLabel: t("settings.dialog.confirm.updatePassword"),
        cancelLabel: t("common.cancel"),
        inputValue: "",
        inputPlaceholder: "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        error: "",
        loading: false,
      });
      return;
    }
    if (actionKey === "request_data") {
      try {
        const res = await settingsService.requestDataExport();
        showToast(res?.detail || t("settings.success.dataExportRequested"), "success");
      } catch (error) {
        showToast(error?.data?.detail || error?.message || t("settings.errors.actionFailed"), "error");
      }
      return;
    }
    if (actionKey === "deactivate") {
      setDialog({
        open: true,
        type: "confirm",
        action: "deactivate_account",
        title: t("settings.dialog.title.deactivateAccount"),
        message: t("settings.dialog.message.deactivateAccount"),
        tone: "warning",
        confirmLabel: t("settings.dialog.confirm.deactivate"),
        cancelLabel: t("common.cancel"),
        inputValue: "",
        inputPlaceholder: "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        error: "",
        loading: false,
      });
      return;
    }
    if (actionKey === "delete_account") {
      setDialog({
        open: true,
        type: "prompt",
        action: "delete_account",
        title: t("settings.dialog.title.deleteAccount"),
        message: t("settings.dialog.message.deleteAccount"),
        tone: "danger",
        confirmLabel: t("settings.dialog.confirm.delete"),
        cancelLabel: t("common.cancel"),
        inputValue: "",
        inputPlaceholder: t("settings.dialog.deleteKeyword"),
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        error: "",
        loading: false,
      });
    }
  };

  const handleSecurityPolicies = () => {
    window.open("/privacy-policy", "_blank", "noopener,noreferrer");
  };

  const handleSecurityDataExport = async () => {
    try {
      const res = await settingsService.requestDataExport();
      showToast(res?.detail || t("settings.success.dataExportRequested"), "success");
    } catch (error) {
      showToast(error?.data?.detail || error?.message || t("settings.errors.dataExportFailed"), "error");
    }
  };

  const handleDialogConfirm = async () => {
    if (!dialog.open || dialog.loading) return;
    if (dialog.type === "alert") {
      closeDialog();
      return;
    }

    if (dialog.action === "add_favorite_material") {
      const value = String(dialog.inputValue || "").trim();
      if (!value) {
        setDialog((prev) => ({ ...prev, error: t("settings.errors.enterMaterialName") }));
        return;
      }
      updateDefaultsState((prev) => {
        const current = Array.isArray(prev.filters?.favorites) ? prev.filters.favorites : [];
        if (current.includes(value)) return prev;
        return {
          ...prev,
          filters: {
            ...(prev.filters || {}),
            favorites: [...current, value],
          },
        };
      });
      closeDialog();
      return;
    }

    if (dialog.action === "set_file_naming") {
      const value = String(dialog.inputValue || "").trim();
      if (!value) {
        setDialog((prev) => ({ ...prev, error: t("settings.errors.fileNamingEmpty") }));
        return;
      }
      updateDefaultsState((prev) => ({
        ...prev,
        exports: { ...(prev.exports || {}), file_naming: value },
      }));
      closeDialog();
      return;
    }

    if (dialog.action === "change_password") {
      if (!dialog.currentPassword || !dialog.newPassword || !dialog.confirmPassword) {
        setDialog((prev) => ({ ...prev, error: t("settings.errors.fillAllPasswordFields") }));
        return;
      }
      if (dialog.newPassword.length < 8) {
        setDialog((prev) => ({ ...prev, error: t("settings.errors.passwordMinLength") }));
        return;
      }
      if (dialog.newPassword !== dialog.confirmPassword) {
        setDialog((prev) => ({ ...prev, error: t("settings.errors.passwordMismatch") }));
        return;
      }
      setDialog((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const res = await settingsService.changePassword({
          current_password: dialog.currentPassword,
          new_password: dialog.newPassword,
        });
        closeDialog();
        showToast(res?.detail || t("settings.success.passwordUpdated"), "success");
      } catch (error) {
        setDialog((prev) => ({
          ...prev,
          loading: false,
          error: error?.data?.detail || error?.message || t("settings.errors.updatePassword"),
        }));
      }
      return;
    }

    if (dialog.action === "deactivate_account") {
      setDialog((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const res = await settingsService.deactivateAccount();
        showToast(res?.detail || t("settings.success.accountDeactivated"), "success");
        clearAuthState();
        window.location.replace("/login");
      } catch (error) {
        setDialog((prev) => ({
          ...prev,
          loading: false,
          error: error?.data?.detail || error?.message || t("settings.errors.deactivateAccount"),
        }));
      }
      return;
    }

    if (dialog.action === "delete_account") {
      if (String(dialog.inputValue || "").trim() !== t("settings.dialog.deleteKeyword")) {
        setDialog((prev) => ({ ...prev, error: t("settings.errors.typeDeleteExactly") }));
        return;
      }
      setDialog((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const res = await settingsService.deleteAccount();
        showToast(res?.detail || t("settings.success.accountDeleted"), "success");
        clearAuthState();
        window.location.replace("/login");
      } catch (error) {
        setDialog((prev) => ({
          ...prev,
          loading: false,
          error: error?.data?.detail || error?.message || t("settings.errors.deleteAccount"),
        }));
      }
    }

    closeDialog();
  };

  const renderSubscription = () => {
    const plan = subscriptionData.currentPlan;
    return (
      <div className="subscription-layout">
        <div className="settings-card glass-card-ui subscription-header">
          <div>
            <div className="settings-card-title">{t("settings.subscription.title")}</div>
            <div className="settings-card-subtitle">{t("settings.subscription.subtitle")}</div>
          </div>
          <div className="settings-chip muted">
            <CreditCard size={16} />
            {t("settings.subscription.billingManaged")}
          </div>
        </div>

        <div className="settings-card glass-card-ui subscription-current">
          <div className="subscription-current-left">
            <div className="sub-plan-heading">
              <div>
                <div className="sub-plan-title">{localizeStatic(plan.name)}</div>
                <div className="sub-plan-subtitle">{localizeStatic(plan.description)}</div>
              </div>
              <span className="settings-pill accent">{t("common.active")}</span>
            </div>
            <div className="sub-plan-meta">
              <div>
                <div className="stat-label">{t("settings.subscription.billingInterval")}</div>
                <div className="sub-plan-value">{localizeStatic(plan.billingInterval)}</div>
              </div>
              <div>
                <div className="stat-label">{t("settings.subscription.nextRenewal")}</div>
                <div className="sub-plan-value">{localizeStatic(plan.nextRenewal)}</div>
              </div>
              <div>
                <div className="stat-label">{t("settings.subscription.users")}</div>
                <div className="sub-plan-value">{localizeStatic(plan.users)}</div>
              </div>
              <div>
                <div className="stat-label">{t("settings.subscription.exportLimit")}</div>
                <div className="sub-plan-value">{localizeStatic(plan.exportLimit)}</div>
              </div>
            </div>
            <div className="sub-plan-actions">
              <button type="button" className="primary-btn">{t("settings.subscription.upgradePlan")}</button>
              <button type="button" className="ghost-button">{t("settings.subscription.manageAddOns")}</button>
              <button type="button" className="ghost-button subtle">{t("settings.subscription.switchToAnnual")}</button>
            </div>
          </div>
          <div className="subscription-price">
            <div className="price-amount">{plan.price}</div>
            <div className="price-cadence">{localizeStatic(plan.cadence)}</div>
          </div>
        </div>

        <div className="settings-card glass-card-ui">
          <div className="settings-card-header">
            <div>
              <div className="settings-card-title">{t("settings.subscription.availablePlans")}</div>
              <div className="settings-card-subtitle">{t("settings.subscription.comparePlans")}</div>
            </div>
          </div>
          <div className="plan-grid">
            {subscriptionData.availablePlans.map((p) => (
              <div key={p.name} className="plan-card">
                <div className="plan-card-top">
                  <div>
                    <div className="plan-name">{localizeStatic(p.name)}</div>
                    <div className="plan-price">{localizeStatic(p.price)}</div>
                    <div className="plan-cadence">{localizeStatic(p.cadence)}</div>
                  </div>
                  {p.tag && <span className="settings-chip success">{localizeStatic(p.tag)}</span>}
                </div>
                <ul className="plan-bullets">
                  {p.bullets.map((b) => (
                    <li key={b}>{localizeStatic(b)}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="settings-card glass-card-ui">
          <div className="settings-card-header">
            <div>
              <div className="settings-card-title">{t("settings.subscription.addOns")}</div>
              <div className="settings-card-subtitle">{t("settings.subscription.boostPlan")}</div>
            </div>
          </div>
          <div className="addon-grid">
            {subscriptionData.addons.map((addon) => (
              <div key={addon.name} className="addon-card">
                <div>
                  <div className="addon-name">{localizeStatic(addon.name)}</div>
                  <div className="addon-price">{localizeStatic(addon.price)}</div>
                  <div className="addon-desc">{localizeStatic(addon.desc)}</div>
                </div>
                <div className={`addon-toggle ${addon.enabled ? "on" : "off"}`}>
                  <div className="toggle-knob" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="subscription-columns">
          <div className="settings-card glass-card-ui billing-card">
            <div className="settings-card-title">{t("settings.subscription.billingDetails")}</div>
            <div className="billing-section">
              <div className="stat-label">{localizeStatic(subscriptionData.billingDetails.addressTitle)}</div>
              <div className="billing-copy">
                {subscriptionData.billingDetails.address.map((line) => (
                  <div key={line}>{localizeStatic(line)}</div>
                ))}
              </div>
              <button type="button" className="link-button">{t("settings.subscription.editAddress")}</button>
            </div>
            <div className="billing-section">
              <div className="stat-label">{localizeStatic(subscriptionData.billingDetails.paymentTitle)}</div>
              <div className="billing-copy">{localizeStatic(subscriptionData.billingDetails.payment)}</div>
              <button type="button" className="link-button">{t("settings.subscription.updatePaymentMethod")}</button>
            </div>
            <div className="billing-section">
              <div className="stat-label">{t("settings.subscription.paymentStatus")}</div>
              <div className="billing-copy success">{localizeStatic(subscriptionData.billingDetails.status)}</div>
              <div className="billing-copy subtle">{localizeStatic(subscriptionData.billingDetails.statusMeta)}</div>
            </div>
            <div className="billing-section">
              <div className="stat-label">{t("settings.subscription.vatNumber")}</div>
              <div className="billing-copy">{subscriptionData.billingDetails.vatNumber}</div>
              <button type="button" className="link-button">{t("settings.subscription.editVatInfo")}</button>
            </div>
          </div>

          <div className="settings-card glass-card-ui invoices-card">
            <div className="settings-card-title">{t("settings.subscription.recentInvoices")}</div>
            <ul className="invoice-list">
              {subscriptionData.invoices.map((invoice) => (
                <li key={invoice.label}>
                  <div>
                    <div className="invoice-date">{localizeStatic(invoice.date)}</div>
                    <div className="invoice-label">{localizeStatic(invoice.label)}</div>
                  </div>
                  <div className="invoice-right">
                    <div className="invoice-amount">{invoice.amount}</div>
                    <span className="settings-chip success small">{t("settings.subscription.paid")}</span>
                  </div>
                </li>
              ))}
            </ul>
            <button type="button" className="link-button">{t("settings.subscription.viewAllInvoices")}</button>
          </div>
        </div>

        <div className="settings-card glass-card-ui danger-card">
          <div className="settings-card-title danger-text">{t("settings.subscription.dangerZone")}</div>
          <div className="danger-row">
            <div>
              <div className="danger-title">{t("settings.subscription.pauseSubscription")}</div>
              <div className="settings-card-subtitle">{t("settings.subscription.pauseSubscriptionSubtitle")}</div>
            </div>
            <button type="button" className="ghost-button subtle">
              <PauseCircle size={16} />
              {t("settings.subscription.pause")}
            </button>
          </div>
          <div className="danger-row">
            <div>
              <div className="danger-title">{t("settings.subscription.cancelSubscription")}</div>
              <div className="settings-card-subtitle">{t("settings.subscription.cancelSubscriptionSubtitle")}</div>
            </div>
            <button type="button" className="danger-btn">
              <XOctagon size={16} />
              {t("settings.subscription.cancelPlan")}
            </button>
          </div>
        </div>

        <div className="settings-card glass-card-ui help-card">
          <div>
            <div className="settings-card-title">{t("settings.subscription.needHelpTitle")}</div>
            <div className="settings-card-subtitle">
              {t("settings.subscription.needHelpSubtitle")}
            </div>
          </div>
          <div className="help-actions">
            <button type="button" className="ghost-button">{t("settings.subscription.contactSales")}</button>
            <button type="button" className="primary-btn subtle">{t("settings.subscription.viewFaq")}</button>
          </div>
        </div>
      </div>
    );
  };

  const renderDefaults = () => (
    <div className="defaults-layout">
      <div className="settings-card glass-card-ui defaults-header">
        <div>
          <div className="settings-card-title">{t("settings.defaults.headerTitle", { defaultValue: "Default Settings" })}</div>
          <div className="settings-card-subtitle">{t("settings.defaults.headerSubtitle", { defaultValue: "Customize your default experience across all features." })}</div>
          {(defaultsLoading || defaultsSaving) && (
            <div className="field-helper mt-1">
              {defaultsLoading ? t("settings.defaults.loadingDefaults") : t("settings.defaults.savingDefaults")}
            </div>
          )}
        </div>
        <div className="defaults-actions">
          <button type="button" className="ghost-button subtle compact" onClick={() => handleResetDefaults("all")}>
            <RefreshCcwDot size={16} />
            {t("settings.defaults.resetAll")}
          </button>
          <button type="button" className="primary-btn compact" onClick={exportDefaultsAsJson}>
            <Download size={16} />
            {t("settings.defaults.export")}
          </button>
        </div>
      </div>

      <div className="settings-card glass-card-ui defaults-section">
        <div className="section-heading">
          <Globe2 size={16} />
          <span>{tr(defaultSettingsData.language.title)}</span>
        </div>
        <div className="language-selector">
          <div>
            <div className="toggle-label">{tr("settings.language.selector.label")}</div>
            <div className="field-helper">{tr("settings.language.selector.helper")}</div>
          </div>
          <div className="language-options">
            {LANGUAGE_CHOICES.map((option) => (
              <button
                key={option.code}
                type="button"
                className={`selector-pill ${getLanguageCodeFromValue(defaultsDraft.language) === option.code ? "selected" : ""}`}
                onClick={() => {
                  const label = option.code === "de" ? "Deutsch" : option.code === "zh" ? "中文" : "English (US)";
                  setDefaultsField("language", label);
                  handleLanguageChange(option.code);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <div className="settings-field-grid defaults-grid">
          {defaultsLanguageFields.map((field) => (
            <div key={field.label} className="settings-field">
              <label>{tr(field.label)}</label>
              <button
                type="button"
                className="settings-value with-select settings-value-button"
                onClick={() => cycleDefaultsField(field.key, DEFAULTS_OPTIONS[field.key] || [field.value])}
              >
                <span>{tr(field.value)}</span>
                <Settings2 size={16} />
              </button>
              {field.helper && <div className="field-helper">{tr(field.helper)}</div>}
            </div>
          ))}
        </div>
      </div>

      <div className="settings-card glass-card-ui defaults-section">
        <div className="section-heading">
          <BarChartBig size={16} />
          <span>{tr(defaultSettingsData.charts.title)}</span>
        </div>
        <div className="settings-field-grid defaults-grid">
          {defaultsChartFields.map((field) => (
            <div key={field.label} className="settings-field">
              <label>{tr(field.label)}</label>
              <button
                type="button"
                className="settings-value with-select settings-value-button"
                onClick={() => cycleDefaultsField(field.key, DEFAULTS_OPTIONS[field.key] || [field.value])}
              >
                <span>{tr(field.value)}</span>
                <Settings2 size={16} />
              </button>
            </div>
          ))}
        </div>
        <div className="toggle-list">
          {defaultsChartToggleFields.map((item) => (
            <div key={item.key} className="toggle-row">
              <div className="toggle-label">{tr(item.label)}</div>
              <button
                type="button"
                className={`addon-toggle ${defaultsDraft.chart_toggles?.[item.key] ? "on" : "off"} small`}
                onClick={() => toggleDefaultsChartFlag(item.key)}
              >
                <div className="toggle-knob" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="settings-card glass-card-ui defaults-section">
        <div className="section-heading">
          <Sparkles size={16} />
          <span>{tr(defaultSettingsData.filters.title)}</span>
        </div>
        <div className="favorites-row">
          <div className="favorites-label">{t("settings.defaults.favorites.label", { defaultValue: "Favorite Materials" })}</div>
          <div className="favorites-chips">
            {(defaultsDraft.filters?.favorites || []).map((mat) => (
              <button
                key={mat}
                type="button"
                className="settings-pill soft"
                onClick={() =>
                  updateDefaultsState((prev) => ({
                    ...prev,
                    filters: {
                      ...(prev.filters || {}),
                      favorites: (prev.filters?.favorites || []).filter((x) => x !== mat),
                    },
                  }))
                }
              >
                {tr(mat)}
              </button>
            ))}
            <button type="button" className="ghost-button subtle compact" onClick={addFavoriteMaterial}>
              {t("settings.defaults.favorites.add", { defaultValue: "+ Add Material" })}
            </button>
          </div>
          <div className="favorites-count">
            {t("settings.defaults.favorites.count", {
              count: (defaultsDraft.filters?.favorites || []).length,
              defaultValue: `${(defaultsDraft.filters?.favorites || []).length} selected`,
            })}
          </div>
        </div>
        <div className="selector-grid">
          <div>
            <div className="selector-title">{t("settings.defaults.filters.regions", { defaultValue: "Default Regions" })}</div>
            <div className="selector-list">
              {(defaultsDraft.filters?.all_regions || []).map((region) => (
                <button
                  key={region}
                  type="button"
                  className={`selector-pill ${(defaultsDraft.filters?.regions || []).includes(region) ? "selected" : ""}`}
                  onClick={() => toggleMultiSelect("regions", region)}
                >
                  {tr(region)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="selector-title">{t("settings.defaults.filters.sectors", { defaultValue: "Default Sectors" })}</div>
            <div className="selector-list">
              {(defaultsDraft.filters?.all_sectors || []).map((sector) => (
                <button
                  key={sector}
                  type="button"
                  className={`selector-pill ${(defaultsDraft.filters?.sectors || []).includes(sector) ? "selected" : ""}`}
                  onClick={() => toggleMultiSelect("sectors", sector)}
                >
                  {tr(sector)}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="toggle-list">
          {defaultsFilterToggleFields.map((item) => (
            <div key={item.key} className="toggle-row">
              <div>
                <div className="toggle-label">{tr(item.label)}</div>
                <div className="field-helper">{tr(item.helper)}</div>
              </div>
              <button
                type="button"
                className={`addon-toggle ${defaultsDraft.filters?.[item.key] ? "on" : "off"} small`}
                onClick={() => toggleDefaultsFilterFlag(item.key)}
              >
                <div className="toggle-knob" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="settings-card glass-card-ui defaults-section">
        <div className="section-heading">
          <Download size={16} />
          <span>{tr(defaultSettingsData.export.title)}</span>
        </div>
        <div className="settings-field-grid defaults-grid">
          {defaultsExportFields.map((field) => (
            <div key={field.label} className="settings-field">
              <label>{tr(field.label)}</label>
              <div className={`settings-value with-select ${field.hasButton ? "with-action" : ""}`}>
                <button
                  type="button"
                  className="settings-value-button-inline"
                  onClick={() => {
                    if (field.key === "format") return cycleDefaultsExportField("format", DEFAULTS_OPTIONS.export_format);
                    if (field.key === "time_period") return cycleDefaultsExportField("time_period", DEFAULTS_OPTIONS.export_time_period);
                    if (field.key === "file_naming") {
                      setDialog({
                        open: true,
                        type: "prompt",
                        action: "set_file_naming",
                        title: t("settings.defaults.fileNamingConvention"),
                        message: t("settings.defaults.setFileNamingFormat"),
                        tone: "primary",
                        confirmLabel: t("common.save"),
                        cancelLabel: t("common.cancel"),
                        inputValue: defaultsDraft.exports?.file_naming || "",
                        inputPlaceholder: t("settings.defaults.fileNamingPlaceholder"),
                        currentPassword: "",
                        newPassword: "",
                        confirmPassword: "",
                        error: "",
                        loading: false,
                      });
                    }
                  }}
                >
                  <span>{tr(field.value)}</span>
                  <Settings2 size={16} />
                </button>
                {field.hasButton && (
                  <button
                    type="button"
                    className="ghost-button subtle compact"
                    onClick={() =>
                      setDialog({
                        open: true,
                        type: "alert",
                        action: "preview_file_naming",
                        title: t("settings.defaults.fileNamingPreview"),
                        message: defaultsDraft.exports?.file_naming || t("settings.defaults.noFileNamingConvention"),
                        tone: "primary",
                        confirmLabel: t("common.close"),
                        cancelLabel: "",
                        inputValue: "",
                        inputPlaceholder: "",
                        currentPassword: "",
                        newPassword: "",
                        confirmPassword: "",
                        error: "",
                        loading: false,
                      })
                    }
                  >
                    {field.hasButton}
                  </button>
                )}
              </div>
              {field.helper && <div className="field-helper">{tr(field.helper)}</div>}
            </div>
          ))}
        </div>
        <div className="toggle-list">
          {defaultsExportToggleFields.map((item) => (
            <div key={item.key} className="toggle-row">
              <div>
                <div className="toggle-label">{tr(item.label)}</div>
                <div className="field-helper">{tr(item.helper)}</div>
              </div>
              <button
                type="button"
                className={`addon-toggle ${defaultsDraft.exports?.[item.key] ? "on" : "off"} small`}
                onClick={() => toggleDefaultsExportFlag(item.key)}
              >
                <div className="toggle-knob" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="settings-card glass-card-ui defaults-section">
        <div className="section-heading">
          <Wand2 size={16} />
          <span>{tr(defaultSettingsData.ai.title)}</span>
        </div>
        <div className="settings-field-grid defaults-grid">
          {defaultsAiFields.map((field) => (
            <div key={field.label} className="settings-field">
              <label>{tr(field.label)}</label>
              <button
                type="button"
                className="settings-value with-select settings-value-button"
                onClick={() =>
                  cycleDefaultsAiField(
                    field.key,
                    field.key === "response_style" ? DEFAULTS_OPTIONS.ai_response_style : DEFAULTS_OPTIONS.ai_language
                  )
                }
              >
                <span>{tr(field.value)}</span>
                <Settings2 size={16} />
              </button>
            </div>
          ))}
        </div>
        <div className="toggle-list">
          {defaultsAiToggleFields.map((item) => (
            <div key={item.key} className="toggle-row">
              <div>
                <div className="toggle-label">{tr(item.label)}</div>
                <div className="field-helper">{tr(item.helper)}</div>
              </div>
              <button
                type="button"
                className={`addon-toggle ${defaultsDraft.ai?.[item.key] ? "on" : "off"} small`}
                onClick={() => toggleDefaultsAiFlag(item.key)}
              >
                <div className="toggle-knob" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="settings-card glass-card-ui defaults-section reset-row">
        <div>
          <div className="settings-card-title">{t("settings.defaults.reset.title", { defaultValue: "Reset to Defaults" })}</div>
          <div className="settings-card-subtitle">{t("settings.defaults.reset.subtitle", { defaultValue: "Restore settings to their original values." })}</div>
        </div>
        <div className="reset-actions">
          {[
            { key: "language", fallback: "Reset Language" },
            { key: "charts", fallback: "Reset Charts" },
            { key: "filters", fallback: "Reset Filters" },
            { key: "exports", fallback: "Reset Exports" },
            { key: "ai", fallback: "Reset AI" },
          ].map(({ key, fallback }) => (
            <button
              key={key}
              type="button"
              className="ghost-button subtle compact"
              onClick={() => handleResetDefaults(key)}
            >
              {t(`settings.defaults.reset.${key}`, { defaultValue: fallback })}
            </button>
          ))}
          <button type="button" className="danger-btn compact" onClick={() => handleResetDefaults("all")}>
            {t("settings.defaults.reset.all", { defaultValue: "Reset Everything" })}
          </button>
        </div>
      </div>
    </div>
  );

  const renderSecurity = () => (
    <div className="security-layout">
      <div className="settings-card glass-card-ui api-header">
        <div>
          <div className="settings-card-title">{t("settings.securityPrivacy.title")}</div>
          <div className="settings-card-subtitle">
            {t("settings.securityPrivacy.subtitle")}
          </div>
        </div>
        <button type="button" className="ghost-button subtle compact" onClick={handleSecurityPolicies}>
          <ShieldAlert size={16} />
          {t("settings.securityPrivacy.policies")}
        </button>
      </div>

      <div className="settings-card glass-card-ui security-section">
        <div className="section-heading">
          <ShieldCheck size={16} />
          <span>{t("settings.securityPrivacy.dataPrivacyGdpr")}</span>
        </div>
        <div className="cta-banner">
          {localizeStatic(securityData.dataPrivacy.rights)}
          <div className="field-helper">{localizeStatic(securityData.dataPrivacy.summary)}</div>
        </div>
        <div className="security-row">
          <div className="toggle-label">{t("settings.securityPrivacy.requestDataOverview")}</div>
          <button type="button" className="ghost-button subtle compact" onClick={handleSecurityDataExport}>
            {t("settings.securityPrivacy.downloadData")}
          </button>
        </div>
        <div className="data-grid">
          {securityDataOverview.map((item) => (
            <div key={item.label} className="stat-row">
              <div className="stat-label">{localizeStatic(item.label)}</div>
              <div className="stat-value">{item.size}</div>
            </div>
          ))}
        </div>
        <div className="danger-row ai-reset-row">
          <div className="danger-title">{t("settings.securityPrivacy.deleteAccount")}</div>
          <button type="button" className="danger-btn compact" onClick={() => handleAccountAction("delete_account")}>
            <Trash2 size={14} />
            {t("settings.securityPrivacy.deleteAccount")}
          </button>
        </div>
      </div>

      <div className="settings-card glass-card-ui security-section">
        <div className="section-heading">
          <GlobeLock size={16} />
          <span>{t("settings.securityPrivacy.dataResidency")}</span>
        </div>
        <div className="toggle-row">
          <div>
            <div className="toggle-label">{t("settings.securityPrivacy.euServersOnly")}</div>
            <div className="field-helper">{t("settings.securityPrivacy.euServersOnlyHelper")}</div>
          </div>
          <div className="addon-toggle on small">
            <div className="toggle-knob" />
          </div>
        </div>
        <div className="resource-grid">
          {securityData.residency.regions.map((region) => (
            <div key={region.label} className="resource-card">
              <div className="resource-title">{localizeStatic(region.label)}</div>
              <div className="resource-desc">{region.code}</div>
              <span className={`status-pill ${region.active ? "ok" : "warn"}`}>{region.active ? t("common.active") : t("common.disabled")}</span>
            </div>
          ))}
        </div>
        <div className="scope-grid">
          {securityData.residency.compliance.map((item) => (
            <span key={item} className="settings-pill soft">{localizeStatic(item)}</span>
          ))}
        </div>
      </div>

      <div className="settings-card glass-card-ui security-section">
        <div className="section-heading">
          <Lock size={16} />
          <span>{t("settings.securityPrivacy.twoFactorAuth")}</span>
        </div>
        <div className="toggle-row">
          <div className="toggle-label">{t("settings.securityPrivacy.twoFactorEnabled")}</div>
          <span className="settings-chip success small">{t("common.active")}</span>
        </div>
        <div className="qr-box">
          <div className="qr-placeholder">▢▢</div>
          <div>
            <div className="toggle-label">{t("settings.securityPrivacy.authenticatorApp")}</div>
            <div className="field-helper">{t("settings.securityPrivacy.authenticatorHelper")}</div>
            <div className="settings-value with-select">
              <span>{securityData.twofa.qr}</span>
            </div>
          </div>
        </div>
        <div className="security-row">
          <div className="toggle-label">{t("settings.securityPrivacy.backupCodes")}</div>
          <div className="field-helper">{t("settings.securityPrivacy.backupCodesAvailable", { count: securityData.twofa.backupCodes })}</div>
          <div className="export-buttons">
            <button type="button" className="ghost-button subtle compact">{t("common.show")}</button>
            <button type="button" className="ghost-button subtle compact">{t("common.regenerate")}</button>
          </div>
        </div>
      </div>

      <div className="settings-card glass-card-ui security-section">
        <div className="section-heading">
          <Smartphone size={16} />
          <span>{t("settings.securityPrivacy.activeSessionsDevices")}</span>
        </div>
        <div className="session-stack">
          {securityData.sessions.map((session) => (
            <div key={session.device} className="session-row">
              <div className="session-meta">
                <div className="session-title">{localizeStatic(session.device)}</div>
                <div className="session-location">{localizeStatic(session.meta)}</div>
                <div className="session-location">{t("settings.securityPrivacy.sessionMeta", { ip: session.ip, last: localizeStatic(session.last) })}</div>
              </div>
              <div className="session-actions">
                {session.current ? <span className="settings-chip success small">{t("settings.securityPrivacy.currentSession")}</span> : <button type="button" className="ghost-button subtle compact">{t("nav.logout")}</button>}
              </div>
            </div>
          ))}
        </div>
        <button type="button" className="danger-btn compact">{t("settings.securityPrivacy.endAll")}</button>
      </div>

      <div className="settings-card glass-card-ui security-section">
        <div className="section-heading">
          <Activity size={16} />
          <span>{t("settings.securityPrivacy.securityLogsActivity")}</span>
        </div>
        <div className="activity-list security-log">
          {securityData.logs.map((log) => (
            <div key={log.title + log.time} className="activity-list-item">
              <span className={`status-dot ${log.tone}`} />
              <div className="activity-copy">
                <div className="activity-title">{localizeStatic(log.title)}</div>
                <div className="activity-meta">{localizeStatic(log.meta)}</div>
              </div>
              <div className="activity-time">{localizeStatic(log.time)}</div>
            </div>
          ))}
        </div>
        <button type="button" className="ghost-button subtle compact">{t("settings.securityPrivacy.viewAllActivity")}</button>
      </div>

      <div className="settings-card glass-card-ui security-section">
        <div className="section-heading">
          <Lock size={16} />
          <span>{t("settings.securityPrivacy.passwordAuthentication")}</span>
        </div>
        <div className="security-row">
          <div>
            <div className="toggle-label">{t("settings.securityPrivacy.changePassword")}</div>
            <div className="field-helper">{t("settings.securityPrivacy.lastChanged", { value: localizeStatic(securityData.password.lastChanged) })}</div>
          </div>
          <button type="button" className="ghost-button subtle compact" onClick={() => handleAccountAction("change_password")}>
            {t("settings.securityPrivacy.changePassword")}
          </button>
        </div>
        <div className="password-rules">
          {securityData.password.rules.map((rule) => (
            <div key={rule} className="rule-item">
              <span className="status-dot good" />
              <span>{localizeStatic(rule)}</span>
            </div>
          ))}
        </div>
        <div className="security-row">
          <div>
            <div className="toggle-label">{t("settings.securityPrivacy.passkeySupport")}</div>
            <div className="field-helper">{t("settings.securityPrivacy.passkeyHelper")}</div>
          </div>
          <button type="button" className="ghost-button subtle compact">{t("settings.securityPrivacy.addPasskey")}</button>
        </div>
      </div>

      <div className="settings-card glass-card-ui security-section">
        <div className="section-heading">
          <Eye size={16} />
          <span>{t("settings.securityPrivacy.privacySettings")}</span>
        </div>
        <div className="privacy-grid">
          {securityData.privacy.map((item) => (
            <div key={item.label} className="privacy-row">
              <div>
                <div className="toggle-label">{localizeStatic(item.label)}</div>
                <div className="field-helper">{localizeStatic(item.helper)}</div>
              </div>
              {item.action ? (
                <button type="button" className="ghost-button subtle compact">{localizeStatic(item.action)}</button>
              ) : (
                <div className={`addon-toggle ${item.toggle ? "on" : "off"} small`}>
                  <div className="toggle-knob" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="settings-card glass-card-ui security-section">
        <div className="section-heading">
          <Clock3 size={16} />
          <span>{t("settings.securityPrivacy.dataRetentionDeletion")}</span>
        </div>
        <div className="settings-field-grid defaults-grid">
          {securityData.retention.slice(0, 2).map((item) => (
            <div key={item.label} className="settings-field">
              <label>{localizeStatic(item.label)}</label>
              <div className="settings-value with-select">
                <span>{localizeStatic(item.value)}</span>
                <Settings2 size={16} />
              </div>
              <div className="field-helper">{localizeStatic(item.helper)}</div>
            </div>
          ))}
        </div>
        <div className="toggle-row">
          <div>
            <div className="toggle-label">{localizeStatic(securityData.retention[2].label)}</div>
            <div className="field-helper">{localizeStatic(securityData.retention[2].helper)}</div>
          </div>
          <div className={`addon-toggle ${securityData.retention[2].toggle ? "on" : "off"} small`}>
            <div className="toggle-knob" />
          </div>
        </div>
      </div>
    </div>
  );

  const renderAiAssistant = () => (
    <div className="ai-layout">
      <div className="settings-card glass-card-ui api-header">
        <div>
          <div className="settings-card-title">{t("settings.ai.title")}</div>
          <div className="settings-card-subtitle">
            {t("settings.ai.subtitle")}
          </div>
        </div>
        <button type="button" className="ghost-button subtle compact">
          <Headphones size={16} />
          {t("common.support")}
        </button>
      </div>

      <div className="settings-card glass-card-ui ai-section">
        <div className="section-heading">
          <MessageSquare size={16} />
          <span>{t("settings.ai.interactionPreferences")}</span>
        </div>
        <div className="settings-field-grid defaults-grid">
          <div className="settings-field">
            <label>{localizeStatic(aiSettings.interaction[0].label)}</label>
            <div className="settings-value with-select">
              <span>{localizeStatic(aiSettings.interaction[0].value)}</span>
              <Settings2 size={16} />
            </div>
            <div className="field-helper">{localizeStatic(aiSettings.interaction[0].helper)}</div>
          </div>
        </div>
        <div className="toggle-list">
          {aiSettings.interaction.slice(1).map((item) => (
            <div key={item.label} className="toggle-row">
              <div>
                <div className="toggle-label">{localizeStatic(item.label)}</div>
                <div className="field-helper">{localizeStatic(item.helper)}</div>
              </div>
              <div className={`addon-toggle ${item.toggle ? "on" : "off"} small`}>
                <div className="toggle-knob" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="settings-card glass-card-ui ai-section">
        <div className="section-heading">
          <Sliders size={16} />
          <span>{t("settings.ai.responseStyleDetail")}</span>
        </div>
        <div className="settings-field-grid defaults-grid">
          {aiSettings.response.slice(0, 2).map((item) => (
            <div key={item.label} className="settings-field">
              <label>{localizeStatic(item.label)}</label>
              <div className="settings-value with-select">
                <span>{localizeStatic(item.value)}</span>
                <Settings2 size={16} />
              </div>
              <div className="field-helper">{localizeStatic(item.helper)}</div>
            </div>
          ))}
        </div>
        <div className="slider-row">
          <div>
            <div className="toggle-label">{localizeStatic(aiSettings.response[2].label)}</div>
            <div className="field-helper">{localizeStatic(aiSettings.response[2].helper)}</div>
          </div>
          <div className="slider-track">
            <div className="slider-fill" style={{ width: `${aiSettings.response[2].slider}%` }} />
            <div className="slider-thumb" style={{ left: `${aiSettings.response[2].slider}%` }} />
          </div>
          <span className="slider-value">{aiSettings.response[2].slider}%</span>
        </div>
      </div>

      <div className="settings-card glass-card-ui ai-section">
        <div className="section-heading">
          <Sparkles size={16} />
          <span>{t("settings.ai.contextAwarenessScope")}</span>
        </div>
        <div className="toggle-list">
          {aiSettings.context.map((item) => (
            <div key={item.label} className="toggle-row">
              <div>
                <div className="toggle-label">{localizeStatic(item.label)}</div>
                <div className="field-helper">{localizeStatic(item.helper)}</div>
              </div>
              <div className={`addon-toggle ${item.toggle ? "on" : "off"} small`}>
                <div className="toggle-knob" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="settings-card glass-card-ui ai-section">
        <div className="section-heading">
          <Zap size={16} />
          <span>{t("settings.ai.actionExecutionUi")}</span>
        </div>
        <div className="cta-banner">
          {t("settings.ai.actionExecutionHelp")}
        </div>
        <div className="action-choice">
          {aiSettings.execution.map((item) => (
            <button
              key={item.label}
              type="button"
              className={`selector-pill ${item.selected ? "selected" : ""}`}
            >
              <div className="action-choice-title">{localizeStatic(item.label)}</div>
              <div className="field-helper">{localizeStatic(item.helper)}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="settings-card glass-card-ui ai-section">
        <div className="section-heading">
          <Sparkles size={16} />
          <span>{t("settings.ai.modelPreferences")}</span>
        </div>
        <div className="model-stack">
          {aiSettings.models.map((model) => (
            <div key={model.label} className="model-card">
              <div className="model-card-top">
                <div className="model-dot" />
                <div className="model-label">{localizeStatic(model.label)}</div>
                {model.status && <span className="settings-chip success small">{localizeStatic(model.status)}</span>}
              </div>
              <div className="model-desc">{localizeStatic(model.desc)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="settings-card glass-card-ui ai-section">
        <div className="section-heading">
          <Globe2 size={16} />
          <span>{t("settings.ai.languageTone")}</span>
        </div>
        <div className="settings-field-grid defaults-grid">
          {aiLanguageTone.map((field) => (
            <div key={field.label} className="settings-field">
              <label>{localizeStatic(field.label)}</label>
              <div className="settings-value with-select">
                <span>{localizeStatic(field.value)}</span>
                <Settings2 size={16} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="settings-card glass-card-ui ai-section">
        <div className="section-heading">
          <CheckCircle2 size={16} />
          <span>{t("settings.ai.trainingConsentFeedback")}</span>
        </div>
        <div className="toggle-list">
          {aiSettings.consent.map((item) => (
            <div key={item.label} className="toggle-row">
              <div>
                <div className="toggle-label">{localizeStatic(item.label)}</div>
                <div className="field-helper">{localizeStatic(item.helper)}</div>
              </div>
              <div className={`addon-toggle ${item.toggle ? "on" : "off"} small`}>
                <div className="toggle-knob" />
              </div>
            </div>
          ))}
        </div>
        <div className="export-row">
          <span className="toggle-label">{t("settings.ai.exportConversations")}</span>
          <div className="export-buttons">
            <button type="button" className="ghost-button subtle compact">{t("common.pdf")}</button>
            <button type="button" className="ghost-button subtle compact">{t("common.json")}</button>
          </div>
        </div>
      </div>

      <div className="settings-card glass-card-ui ai-section">
        <div className="section-heading">
          <RefreshCcwDot size={16} />
          <span>{t("settings.ai.onboardingAssistantReset")}</span>
        </div>
        <div className="reset-row ai-reset-row">
          <div>
            <div className="settings-card-title">{t("settings.ai.repeatOnboarding")}</div>
            <div className="settings-card-subtitle">{t("settings.ai.repeatOnboardingSubtitle")}</div>
          </div>
          <button type="button" className="ghost-button subtle compact">{t("settings.ai.startOnboarding")}</button>
        </div>
        <div className="reset-row ai-reset-row">
          <div>
            <div className="settings-card-title">{t("settings.ai.resetAssistantMemory")}</div>
            <div className="settings-card-subtitle">{t("settings.ai.resetAssistantMemorySubtitle")}</div>
          </div>
          <button type="button" className="danger-btn compact">{t("settings.ai.resetMemory")}</button>
        </div>
      </div>
    </div>
  );

  const renderApi = () => (
    <div className="api-layout">
      <div className="settings-card glass-card-ui api-header">
        <div>
          <div className="settings-card-title">{t("settings.api.title")}</div>
          <div className="settings-card-subtitle">{t("settings.api.subtitle")}</div>
        </div>
        <button type="button" className="ghost-button subtle compact">
          <BookOpen size={16} />
          {t("settings.api.documentation")}
        </button>
      </div>

      <div className="settings-card glass-card-ui api-section">
        <div className="section-heading">
          <Link2 size={16} />
          <span>{t("settings.api.overview")}</span>
        </div>
        <div className="api-toggle-list">
          {apiData.overview.map((item) => (
            <div key={item.name} className="api-toggle-row">
              <div>
                <div className="toggle-label">{localizeStatic(item.name)}</div>
                <div className="field-helper">{localizeStatic(item.desc)}</div>
              </div>
              <div className={`addon-toggle ${item.enabled ? "on" : "off"} small`}>
                <div className="toggle-knob" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="settings-card glass-card-ui api-section">
        <div className="api-section-header">
          <div className="section-heading">
            <KeyRound size={16} />
            <span>{t("settings.api.keyManagement")}</span>
          </div>
          <button type="button" className="primary-btn compact">{t("settings.api.createNewKey")}</button>
        </div>
        <div className="api-key-list">
          {apiData.keys.map((key) => (
            <div key={key.name} className="api-key-card">
              <div className="api-key-top">
                <div>
                  <div className="api-key-title">{localizeStatic(key.name)}</div>
                  <span className="settings-chip success small">{localizeStatic(key.status)}</span>
                </div>
                <div className="api-key-meta">{t("settings.api.accessCreated", { access: localizeStatic(key.access), created: key.created })}</div>
              </div>
              <div className="api-key-value">{key.key}</div>
              <div className="api-key-footer">
                <div className="api-key-last">{t("settings.api.lastUsed", { value: localizeStatic(key.lastUsed) })}</div>
                <div className="api-key-actions">
                  <button type="button" className="ghost-button subtle compact">
                    <Copy size={14} /> {t("common.copy")}
                  </button>
                  <button type="button" className="ghost-button subtle compact">
                    <Download size={14} /> {t("settings.api.rotate")}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="settings-card glass-card-ui api-section">
        <div className="section-heading">
          <Activity size={16} />
          <span>{t("settings.api.usageQuotas")}</span>
        </div>
        <div className="quota-grid">
          <div className="quota-block">
            <div className="quota-label">{t("settings.api.requestsToday")}</div>
            <div className="quota-value">{apiData.usage.requestsToday.current.toLocaleString()}</div>
            <div className="quota-helper">{t("settings.api.ofValue", { value: apiData.usage.requestsToday.limit.toLocaleString() })}</div>
          </div>
          <div className="quota-block">
            <div className="quota-label">{t("settings.api.dataTransfer")}</div>
            <div className="quota-value">{apiData.usage.dataTransfer.current}</div>
            <div className="quota-helper">{t("settings.api.ofValue", { value: apiData.usage.dataTransfer.limit })}</div>
          </div>
          <div className="quota-block">
            <div className="quota-label">{t("settings.api.rateLimit")}</div>
            <div className="quota-value">{apiData.usage.rateLimit.current}</div>
            <div className="quota-helper">{t("settings.api.ofValue", { value: apiData.usage.rateLimit.limit })}</div>
          </div>
        </div>
        <div className="bar-list">
          {apiData.usage.monthly.map((item) => (
            <div key={item.label} className="bar-row">
              <div className="bar-label">
                <span>{localizeStatic(item.label)}</span>
                <span className="bar-percent">{item.percent}%</span>
              </div>
              <div className="bar-track">
                <div className="bar-fill" style={{ width: `${item.percent}%`, background: item.color }} />
              </div>
              <div className="bar-helper">{item.value.toLocaleString()}</div>
            </div>
          ))}
        </div>
        <div className="warning-banner">
          <CircleAlert size={16} />
          <div>
            <div className="warning-title">{t("settings.api.approachingRateLimit")}</div>
            <div className="warning-text">{localizeStatic(apiData.usage.warning)}</div>
          </div>
        </div>
      </div>

      <div className="settings-card glass-card-ui api-section">
        <div className="section-heading">
          <Users size={16} />
          <span>{t("settings.api.accessRoles")}</span>
        </div>
        <div className="api-toggle-list">
          {apiData.roles.map((role) => (
            <div key={role.name} className="api-toggle-row">
              <div>
                <div className="toggle-label">{localizeStatic(role.name)}</div>
                <div className="field-helper">{localizeStatic(role.desc)}</div>
              </div>
              <div className={`addon-toggle ${role.enabled ? "on" : "off"} small`}>
                <div className="toggle-knob" />
              </div>
            </div>
          ))}
        </div>
        <div className="scope-grid">
          {apiData.scopes.map((scope) => (
            <label key={scope.label} className={`scope-pill ${scope.enabled ? "selected" : ""}`}>
              <input type="checkbox" checked={scope.enabled} readOnly />
              <span>{localizeStatic(scope.label)}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="settings-card glass-card-ui api-section">
        <div className="section-heading">
          <Terminal size={16} />
          <span>{t("settings.api.developerResources")}</span>
        </div>
        <div className="resource-grid">
          {apiData.resources.map((res) => (
            <div key={res.title} className="resource-card">
              <div className="resource-title">{localizeStatic(res.title)}</div>
              <div className="resource-desc">{localizeStatic(res.desc)}</div>
              <button type="button" className="ghost-button subtle compact">{localizeStatic(res.cta)} →</button>
            </div>
          ))}
        </div>
        <div className="quickstart">
          <div className="quickstart-title">{t("settings.api.quickStart")}</div>
          <pre className="quickstart-code">
            <code>{apiData.quickstart}</code>
          </pre>
        </div>
      </div>

      <div className="settings-card glass-card-ui api-section">
        <div className="section-heading">
          <Clock3 size={16} />
          <span>{t("settings.api.auditLogs")}</span>
        </div>
        <div className="audit-table">
          <div className="audit-head">
            <div>{t("settings.api.timestamp")}</div>
            <div>{t("settings.api.apiKey")}</div>
            <div>{t("settings.api.endpoint")}</div>
            <div>{t("settings.api.ipAddress")}</div>
            <div>{t("settings.api.status")}</div>
          </div>
          {apiData.auditLogs.map((log) => (
            <div key={`${log.ts}-${log.endpoint}`} className="audit-row">
              <div>{log.ts}</div>
              <div>{log.key}</div>
              <div>{log.endpoint}</div>
              <div>{log.ip}</div>
              <div className={`status-pill ${log.status === 200 ? "ok" : "warn"}`}>{log.status}</div>
            </div>
          ))}
        </div>
        <button type="button" className="ghost-button subtle compact">{t("settings.api.exportLogs")}</button>
      </div>
    </div>
  );

  const renderSecondaryPanel = () => {
    const panel = secondaryPanels[activeTab];
    if (!panel) return null;
    return (
      <div className="settings-card glass-card-ui">
        <div className="settings-card-header">
          <div>
            <div className="settings-card-title">{tr(activeTabMeta?.labelKey || activeTabMeta?.label)}</div>
            <div className="settings-card-subtitle">{localizeStatic(panel.description)}</div>
          </div>
          <span className="settings-chip muted">{t("settings.workspacePreview")}</span>
        </div>
        <div className="settings-highlight-grid">
          {(panel.highlights || []).map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.title} className="settings-highlight">
                <div className="settings-highlight-icon">
                  <Icon size={18} />
                </div>
                <div className="settings-highlight-title">{localizeStatic(item.title)}</div>
                <div className="settings-highlight-text">{localizeStatic(item.text)}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="main__content__box settings-page">
      <div className="container-fluid">
        <div className="settings-hero glass-card-ui">
          <div>
            <div className="settings-eyebrow">{tr("settings.hero.eyebrow")}</div>
            <h1 className="settings-title">{tr("settings.hero.title")}</h1>
            <p className="settings-description">
              {tr("settings.hero.description")}
            </p>
            <div className="settings-hero-chips">
              <span className="settings-chip success">
                <BadgeCheck size={16} />
                {t("settings.hero.identityVerified")}
              </span>
              <span className="settings-chip muted">
                <ShieldCheck size={16} />
                {t("settings.hero.securityPostureStrong")}
              </span>
            </div>
          </div>
          <div className="settings-hero-meta">
            <div className="settings-hero-stat">
              <div className="settings-hero-label">{t("settings.hero.activeSessions")}</div>
              <div className="settings-hero-value">2</div>
            </div>
            <div className="settings-hero-divider" />
            <div className="settings-hero-stat">
              <div className="settings-hero-label">{t("settings.hero.profileCompletion")}</div>
              <div className="settings-hero-value accent">94%</div>
            </div>
          </div>
        </div>

        <div className="settings-tabs">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const label = t(tab.labelKey || tab.label, { defaultValue: tab.label });
            return (
              <button
                key={tab.key}
                type="button"
                className={`settings-tab ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
                aria-label={label}
              >
                <Icon size={18} />
                <span>{label}</span>
              </button>
            );
          })}
        </div>

        {activeTab === "account" ? (
          <>
            <div className="settings-row">
              <div className="settings-main">
                <div className="settings-card glass-card-ui">
                  <div className="settings-card-header">
                    <div>
                      <div className="settings-card-title">{t("settings.account.userInfo.title")}</div>
                      <div className="settings-card-subtitle">{t("settings.account.userInfo.subtitle")}</div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {accountEditMode && (
                        <>
                          <button
                            type="button"
                            className="ghost-button subtle compact"
                            onClick={() => {
                              setAccountDraft(accountProfile);
                              setAccountEditMode(false);
                              setAccountError("");
                            }}
                            disabled={accountSaving}
                          >
                            {t("common.cancel")}
                          </button>
                          <button
                            type="button"
                            className="primary-btn compact"
                            onClick={handleSaveAccount}
                            disabled={accountSaving}
                          >
                            {accountSaving ? t("common.saving") : t("common.save")}
                          </button>
                        </>
                      )}
                      {!accountEditMode && (
                        <button
                          type="button"
                          className="settings-edit-btn"
                          onClick={() => setAccountEditMode(true)}
                        >
                          <Edit3 size={16} />
                          {t("common.edit")}
                        </button>
                      )}
                    </div>
                  </div>
                  {accountLoading && <div className="field-helper mb-2">{t("settings.account.loading")}</div>}
                  {accountError && <div className="text-danger mb-2">{accountError}</div>}
                  {accountSuccess && <div className="text-success mb-2">{accountSuccess}</div>}
                  <div className="settings-field-grid account-field-grid">
                    {userInformation.map((item) => (
                      <div
                        key={item.label}
                        className={`settings-field ${item.key === "email" ? "settings-field--wide" : ""}`}
                      >
                        <label>{t(item.labelKey, { defaultValue: item.label })}</label>
                        {accountEditMode ? (
                          <input
                            type="text"
                            className="form-control"
                            value={item.value}
                            onChange={(e) => handleAccountInputChange(item.key, e.target.value)}
                          />
                        ) : (
                          <div className="settings-value">{item.value || "—"}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="settings-card glass-card-ui profile-card">
                  <div className="settings-card-header">
                    <div>
                      <div className="settings-card-title">{t("settings.account.profile.title")}</div>
                      <div className="settings-card-subtitle">{t("settings.account.profile.subtitle")}</div>
                    </div>
                    <button type="button" className="settings-edit-btn">
                      <Edit3 size={16} />
                      {t("common.edit")}
                    </button>
                  </div>
                  <div className="profile-meta">
                    <div className="profile-avatar">
                      <img src={avatar} alt={t("settings.account.profile.avatarAlt")} />
                    </div>
                    <div className="profile-bio">{profileMeta.bio}</div>
                  </div>
                  <div className="settings-field-grid">
                    <div className="settings-field">
                      <label>{t("settings.account.profile.industryFocus")}</label>
                      <div className="settings-value">{profileMeta.industryFocus}</div>
                    </div>
                    <div className="settings-field">
                      <label>{t("settings.account.profile.location")}</label>
                      <div className="settings-value">{profileMeta.location}</div>
                    </div>
                  </div>
                  <div className="settings-tag-block">
                    <div className="tag-label">{t("settings.account.profile.expertiseAreas")}</div>
                    <div className="settings-tags">
                      {profileMeta.expertiseAreas.map((item) => (
                        <span key={item} className="settings-pill soft">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="settings-tag-block">
                    <div className="tag-label">{t("settings.account.profile.professionalInterests")}</div>
                    <div className="settings-tags">
                      {profileMeta.interests.map((item) => (
                        <span key={item} className="settings-pill accent">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="settings-card glass-card-ui">
                  <div className="settings-card-header">
                    <div>
                      <div className="settings-card-title">{tr("settings.preferences.title")}</div>
                      <div className="settings-card-subtitle">{tr("settings.preferences.subtitle")}</div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {preferencesEditMode ? (
                        <>
                          <button
                            type="button"
                            className="ghost-button subtle compact"
                            onClick={() => {
                              setPreferencesDraft({
                                ...defaultAccountProfile.preferences,
                                ...(accountProfile.preferences || {}),
                              });
                              setPreferencesEditMode(false);
                            }}
                            disabled={preferencesSaving}
                          >
                            {t("common.cancel")}
                          </button>
                          <button
                            type="button"
                            className="primary-btn compact"
                            onClick={handleSavePreferences}
                            disabled={preferencesSaving}
                          >
                            {preferencesSaving ? t("common.saving") : t("common.save")}
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="settings-edit-btn"
                          onClick={() => setPreferencesEditMode(true)}
                        >
                          <Edit3 size={16} />
                          {t("common.edit")}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="settings-field-grid">
                    {preferencesWithLanguage.map((item) => (
                      <div key={item.key} className="settings-field">
                        <label>{t(item.labelKey, { defaultValue: item.label })}</label>
                        {item.key === "language" ? (
                          <div className="settings-value language-value">
                            <div>{item.value}</div>
                            <div className="language-options">
                              {LANGUAGE_CHOICES.map((option) => (
                                <button
                                  key={option.code}
                                  type="button"
                                  className={`selector-pill ${getLanguageCodeFromValue(preferencesDraft.language) === option.code ? "selected" : ""}`}
                                  onClick={() => handleLanguageChange(option.code)}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                            <div className="field-helper">{tr("settings.language.helper")}</div>
                          </div>
                        ) : preferencesEditMode ? (
                          <input
                            type="text"
                            className="form-control"
                            value={
                              item.key === "timezone"
                                ? preferencesDraft.timezone || ""
                                : item.key === "date_format"
                                ? preferencesDraft.date_format || ""
                                : preferencesDraft.currency || ""
                            }
                            onChange={(e) => {
                              const v = e.target.value;
                              setPreferencesDraft((prev) => ({
                                ...prev,
                                ...(item.key === "timezone"
                                  ? { timezone: v }
                                  : item.key === "date_format"
                                  ? { date_format: v }
                                  : { currency: v }),
                              }));
                            }}
                          />
                        ) : (
                          <div className="settings-value">{item.value}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="settings-card glass-card-ui">
                  <div className="settings-card-header">
                    <div>
                      <div className="settings-card-title">{t("settings.activity.title")}</div>
                      <div className="settings-card-subtitle">{t("settings.activity.subtitle")}</div>
                    </div>
                  </div>
                  <div className="activity-grid">
                    <div>
                      <div className="section-label">{t("settings.activity.recent")}</div>
                      {activityLoading && <div className="field-helper mb-2">{t("settings.activity.loading")}</div>}
                      <ul className="activity-list">
                        {recentActivity.map((item) => (
                          <li key={item.id || `${item.title}-${item.ts}`}>
                            <div className="activity-copy">
                              <div className="activity-title">{localizeActivityTitle(item)}</div>
                              <div className="activity-meta">{localizeActivityMeta(item)}</div>
                            </div>
                            <div className="activity-time">{formatRelativeTime(item.ts)}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="section-label">{t("settings.activity.sessions")}</div>
                      <div className="session-list">
                        {activeSessions.map((session) => {
                          const Icon = /mobile/i.test(String(session.device || "")) ? Smartphone : Laptop2;
                          return (
                            <div key={session.id || session.device} className="session-row">
                              <div className="session-meta">
                                <span className="session-icon">
                                  <Icon size={16} />
                                </span>
                                <div>
                                  <div className="session-title">{localizeSessionDevice(session)}</div>
                                  <div className="session-location">
                                    {localizeSessionLocation(session)}
                                    {session.last_seen ? ` • ${formatRelativeTime(session.last_seen)}` : ""}
                                  </div>
                                </div>
                              </div>
                            <button
                              type="button"
                              className={`session-pill ${session.current ? "good" : "danger"}`}
                              onClick={() => handleRevokeSession(session)}
                              disabled={session.current}
                            >
                              {session.current ? t("settings.activity.current") : t("settings.activity.revoke")}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      className="ghost-button mt-2"
                        onClick={() =>
                          settingsService
                            .revokeOtherSessions()
                            .then(loadActivity)
                            .catch((error) => showToast(error?.data?.detail || error?.message || t("settings.activity.revokeFailed"), "error"))
                        }
                      >
                        {t("settings.activity.revokeOther")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="settings-side">
                <div className="settings-card glass-card-ui">
                  <div className="settings-card-header">
                  <div className="settings-card-title">{t("settings.account.actions.title")}</div>
                  </div>
                  <div className="action-stack">
                    {accountActions.map((action) => {
                      const Icon = action.icon;
                      return (
                        <button
                          key={action.key}
                          type="button"
                          className={`action-button ${action.tone}`}
                          onClick={() => handleAccountAction(action.key)}
                        >
                          <div className="action-copy">
                            <div className="action-title">{t(action.labelKey, { defaultValue: action.label })}</div>
                            <div className="action-desc">{t(action.descKey, { defaultValue: action.description })}</div>
                          </div>
                          <Icon size={18} />
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="settings-card glass-card-ui">
                <div className="settings-card-header">
                  <div className="settings-card-title">{t("settings.security.title")}</div>
                </div>
                <div className="status-list">
                  {securityChecks.map((item) => (
                    <div key={item.label} className="status-row">
                      <div className="status-label">
                        <span className={`status-dot ${item.tone}`} />
                        {t(item.labelKey, { defaultValue: item.label })}
                      </div>
                      <span className={`status-value ${item.tone}`}>{item.value}</span>
                    </div>
                  ))}
                </div>
                <button type="button" className="ghost-button" onClick={() => setActiveTab("security")}>
                  {t("settings.security.viewSettings")}
                </button>
              </div>

              <div className="settings-card glass-card-ui">
                <div className="settings-card-header">
                  <div className="settings-card-title">{t("settings.account.stats.title")}</div>
                </div>
                <div className="stats-grid">
                  {accountStats.map((stat) => (
                    <div key={stat.label} className="stat-row">
                      <div className="stat-label">{t(stat.labelKey, { defaultValue: stat.label })}</div>
                      <div className="stat-value">{stat.value}</div>
                    </div>
                  ))}
                </div>
              </div>
              </div>
            </div>

          </>
        ) : activeTab === "subscription" ? (
          renderSubscription()
        ) : activeTab === "defaults" ? (
          renderDefaults()
        ) : activeTab === "api" ? (
          renderApi()
        ) : activeTab === "ai" ? (
          renderAiAssistant()
        ) : activeTab === "security" ? (
          renderSecurity()
        ) : (
          renderSecondaryPanel()
        )}

        {toast && (
          <div className={`settings-toast ${toast.tone || "info"}`} role="status" aria-live="polite">
            {toast.message}
          </div>
        )}

        {dialog.open && (
          <div className="settings-dialog-backdrop" role="presentation">
            <div className={`settings-dialog ${dialog.tone || "primary"}`} role="dialog" aria-modal="true" aria-label={dialog.title || t("common.dialog")}>
              <div className="settings-dialog-title">{dialog.title}</div>
              {dialog.message && <div className="settings-dialog-message">{dialog.message}</div>}

              {dialog.type === "prompt" && (
                <input
                  type="text"
                  className="form-control settings-dialog-input"
                  placeholder={dialog.inputPlaceholder || ""}
                  value={dialog.inputValue || ""}
                  onChange={(e) => setDialog((prev) => ({ ...prev, inputValue: e.target.value, error: "" }))}
                />
              )}

              {dialog.type === "password" && (
                <div className="settings-dialog-password-grid">
                  <input
                    type="password"
                    className="form-control settings-dialog-input"
                    placeholder={t("settings.dialog.currentPassword")}
                    value={dialog.currentPassword || ""}
                    onChange={(e) => setDialog((prev) => ({ ...prev, currentPassword: e.target.value, error: "" }))}
                  />
                  <input
                    type="password"
                    className="form-control settings-dialog-input"
                    placeholder={t("settings.dialog.newPassword")}
                    value={dialog.newPassword || ""}
                    onChange={(e) => setDialog((prev) => ({ ...prev, newPassword: e.target.value, error: "" }))}
                  />
                  <input
                    type="password"
                    className="form-control settings-dialog-input"
                    placeholder={t("settings.dialog.confirmPassword")}
                    value={dialog.confirmPassword || ""}
                    onChange={(e) => setDialog((prev) => ({ ...prev, confirmPassword: e.target.value, error: "" }))}
                  />
                </div>
              )}

              {dialog.error && <div className="settings-dialog-error">{dialog.error}</div>}

              <div className="settings-dialog-actions">
                {dialog.cancelLabel ? (
                  <button type="button" className="ghost-button subtle compact" onClick={closeDialog} disabled={dialog.loading}>
                    {dialog.cancelLabel || t("common.cancel")}
                  </button>
                ) : null}
                <button
                  type="button"
                  className={dialog.tone === "danger" ? "danger-btn compact" : "primary-btn compact"}
                  onClick={handleDialogConfirm}
                  disabled={dialog.loading}
                >
                  {dialog.loading ? t("common.working") : dialog.confirmLabel || t("common.confirm")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
